import React, { useState, useEffect } from "react";

/* ─────────────────────────────────────────────
    PASTE YOUR SUPABASE PUBLISHABLE KEY BELOW
───────────────────────────────────────────── */
const SUPABASE_URL = "https://tjawaqchdqwzseqvkmcm.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRqYXdhcWNoZHF3enNlcXZrbWNtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY3MjA3MzUsImV4cCI6MjA5MjI5NjczNX0.p2KjRvwQ3173jSpRpPslFRh5EaenbCDq5Lcok3Xb-pU";

/* ─────────────────────────────────────────────
   PASSWORD HASHING
───────────────────────────────────────────── */
async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + "hirehero_salt_2026");
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

/* ─────────────────────────────────────────────
   SUPABASE CLIENT
───────────────────────────────────────────── */
const sb = {
  async query(table, method = "GET", body = null, filters = "") {
    const url = `${SUPABASE_URL}/rest/v1/${table}${filters}`;
    const res = await fetch(url, {
      method,
      headers: {
        "apikey": SUPABASE_KEY,
        "Authorization": `Bearer ${SUPABASE_KEY}`,
        "Content-Type": "application/json",
        "Prefer": method === "POST" ? "return=representation" : "return=minimal",
      },
      body: body ? JSON.stringify(body) : null,
    });
    if (!res.ok) { const e = await res.text(); throw new Error(e); }
    if (method === "DELETE" || method === "PATCH" || res.status === 204) return true;
    const text = await res.text();
    return text ? JSON.parse(text) : [];
  },
  async select(table, filters = "") { return this.query(table, "GET", null, filters); },
  async insert(table, data) { return this.query(table, "POST", data); },
  async update(table, data, filters) { return this.query(table, "PATCH", data, filters); },
  async delete(table, filters) {
    const url = `${SUPABASE_URL}/rest/v1/${table}${filters}`;
    const res = await fetch(url, {
      method: "DELETE",
      headers: {
        "apikey": SUPABASE_KEY,
        "Authorization": `Bearer ${SUPABASE_KEY}`,
        "Content-Type": "application/json",
        "Prefer": "return=minimal",
      },
    });
    return res.ok;
  },
};

/* ─────────────────────────────────────────────
   THEME
───────────────────────────────────────────── */
const LIGHT = {
  bg: "#f0f4f8", surface: "#ffffff", surface2: "#f7f9fc",
  border: "#e2e8f0", text: "#0f172a", muted: "#64748b",
  accent: "#1e3a5f", accentLight: "#2d5a8e", accentGlow: "rgba(30,58,95,0.12)",
  gold: "#f59e0b", goldLight: "#fbbf24", goldGlow: "rgba(245,158,11,0.15)",
  green: "#059669", greenBg: "#ecfdf5", greenBorder: "#6ee7b7",
  red: "#dc2626", redBg: "#fef2f2",
  card: "#ffffff", cardBorder: "#e2e8f0",
  topbar: "rgba(255,255,255,0.92)",
  shadow: "0 4px 24px rgba(15,23,42,0.08)",
  shadowLg: "0 12px 40px rgba(15,23,42,0.12)",
};

const CATEGORIES = [
  { id: "handyman", label: "Handyman", color: "#3b82f6" },
  { id: "power_washing", label: "Power Washing", color: "#06b6d4" },
  { id: "painting", label: "Painting", color: "#f59e0b" },
  { id: "junk_removal", label: "Junk Removal", color: "#ef4444" },
];

const DARK = {
  bg: "#0d1117", surface: "#161b22", surface2: "#1c2333",
  border: "#30363d", text: "#e6edf3", muted: "#8b949e",
  accent: "#58a6ff", accentLight: "#79c0ff", accentGlow: "rgba(88,166,255,0.15)",
  gold: "#f0b429", goldLight: "#fbbf24", goldGlow: "rgba(240,180,41,0.15)",
  green: "#3fb950", greenBg: "rgba(63,185,80,0.1)", greenBorder: "#3fb95060",
  red: "#f85149", redBg: "rgba(248,81,73,0.1)",
  card: "#161b22", cardBorder: "#30363d",
  topbar: "rgba(13,17,23,0.95)",
  shadow: "0 4px 24px rgba(0,0,0,0.4)",
  shadowLg: "0 12px 40px rgba(0,0,0,0.5)",
};

/* ─────────────────────────────────────────────
   PHOTO UPLOAD TO SUPABASE STORAGE
───────────────────────────────────────────── */
async function uploadPhoto(file, folder = "jobs") {
  try {
    const ext = file.name.split(".").pop();
    const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const res = await fetch(`${SUPABASE_URL}/storage/v1/object/uploads/${fileName}`, {
      method: "POST",
      headers: {
        "apikey": SUPABASE_KEY,
        "Authorization": `Bearer ${SUPABASE_KEY}`,
        "Content-Type": file.type,
        "x-upsert": "true",
      },
      body: file,
    });
    if (!res.ok) throw new Error("Upload failed");
    return `${SUPABASE_URL}/storage/v1/object/public/uploads/${fileName}`;
  } catch (e) {
    console.error("Photo upload error:", e);
    return null;
  }
}

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const C_STEPS = ["Account", "Profile", "Verify", "Done"];
const H_STEPS = ["Account", "Address", "Done"];

const UNSPLASH = {
  hero: "https://images.pexels.com/photos/280229/pexels-photo-280229.jpeg?auto=compress&cs=tinysrgb&w=800",
  tools: "https://images.pexels.com/photos/1249611/pexels-photo-1249611.jpeg?auto=compress&cs=tinysrgb&w=600",
  powerwash: "https://images.pexels.com/photos/4108715/pexels-photo-4108715.jpeg?auto=compress&cs=tinysrgb&w=600",
  home: "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=600",
  handyman1: "https://images.pexels.com/photos/3862132/pexels-photo-3862132.jpeg?auto=compress&cs=tinysrgb&w=400",
  handyman2: "https://images.pexels.com/photos/2760243/pexels-photo-2760243.jpeg?auto=compress&cs=tinysrgb&w=400",
  handyman3: "https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg?auto=compress&cs=tinysrgb&w=400",
  job1: "https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=400",
  job2: "https://images.pexels.com/photos/271667/pexels-photo-271667.jpeg?auto=compress&cs=tinysrgb&w=400",
  job3: "https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg?auto=compress&cs=tinysrgb&w=400",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Syne:wght@700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Plus Jakarta Sans',sans-serif;-webkit-tap-highlight-color:transparent;transition:background 0.3s;}
  input,textarea,select,button{font-family:'Plus Jakarta Sans',sans-serif;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes pop{0%{transform:scale(0.88);opacity:0}70%{transform:scale(1.04)}100%{transform:scale(1);opacity:1}}
  @keyframes shimmer{0%{opacity:0.5}50%{opacity:1}100%{opacity:0.5}}
  @keyframes slide{from{transform:translateX(-8px);opacity:0}to{transform:translateX(0);opacity:1}}
  .fu{animation:fadeUp 0.4s cubic-bezier(0.16,1,0.3,1) forwards;}
  .fi{animation:fadeIn 0.3s ease forwards;}
  .pop{animation:pop 0.5s cubic-bezier(0.16,1,0.3,1) forwards;}
  .spin{display:inline-block;animation:spin 0.9s linear infinite;}
  .slide{animation:slide 0.3s ease forwards;}
  textarea{resize:vertical;}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-thumb{border-radius:4px;}
  img{display:block;}
`;

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const session = {
  get: () => { try { return JSON.parse(localStorage.getItem("hh_user")); } catch { return null; } },
  set: (u) => { try { localStorage.setItem("hh_user", JSON.stringify(u)); } catch {} },
  clear: () => { try { localStorage.removeItem("hh_user"); } catch {} },
};

const getTheme = () => "dark";
const setTheme = (t) => { try { localStorage.setItem("hh_theme", t); } catch {} };

function timeAgo(ts) {
  const d = Date.now() - new Date(ts).getTime();
  if (d < 60000) return "just now";
  if (d < 3600000) return Math.floor(d / 60000) + "m ago";
  if (d < 86400000) return Math.floor(d / 3600000) + "h ago";
  return Math.floor(d / 86400000) + "d ago";
}

/* ─────────────────────────────────────────────
   UI COMPONENTS
───────────────────────────────────────────── */
function Logo({ T, size = 22 }) {
  return (
    <span style={{
      fontFamily: "'Syne',sans-serif", fontSize: size + 4,
      fontWeight: 800, color: T.text, letterSpacing: -0.5
    }}>Hire<span style={{ color: T.accent }}>Hero</span></span>
  );
}

function ThemeToggle({ dark, onToggle, T }) { return null; } function ThemeToggleDEAD({ dark, onToggle, T }) {
  return (
    <button onClick={onToggle} style={{
      width: 52, height: 28, borderRadius: 14,
      background: dark ? T.accent : T.border,
      border: "none", cursor: "pointer",
      position: "relative", transition: "background 0.3s",
      flexShrink: 0,
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: "50%",
        background: T.surface, position: "absolute",
        top: 3, left: dark ? 27 : 3,
        transition: "left 0.3s", display: "flex",
        alignItems: "center", justifyContent: "center", fontSize: 12,
      }}>{dark ? "" : ""}</div>
    </button>
  );
}

function Topbar({ T, user, onLogout, dark, onToggleTheme, onBack, onHome, onSwitch, notifications, onNotifClick }) {
  const unread = (notifications || []).filter(n => !n.read).length;
  const [showNotifs, setShowNotifs] = useState(false);

  return (
    <div style={{ position: "sticky", top: 0, zIndex: 100, background: T.topbar, backdropFilter: "blur(16px)", borderBottom: `1px solid ${T.border}`, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: T.shadow }}>
      {onBack
        ? <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700, color: T.accent, padding: "4px 0", minHeight: 44, minWidth: 44 }}>← Back</button>
        : <Logo T={T} size={18} />
      }
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ThemeToggle dark={dark} onToggle={onToggleTheme} T={T} />
        {notifications && (
          <div style={{ position: "relative" }}>
            <button onClick={() => { setShowNotifs(p => !p); if (onNotifClick) onNotifClick(); }} style={{ minWidth: 40, minHeight: 40, borderRadius: 8, background: T.surface2, border: `1.5px solid ${T.border}`, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
              {unread > 0 && <span style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: "50%", background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{unread}</span>}
              {unread > 0 ? "🔔" : "🔕"}
            </button>
            {showNotifs && (
              <div style={{ position: "absolute", right: 0, top: 48, width: 300, background: T.card, borderRadius: 14, border: `1px solid ${T.border}`, boxShadow: T.shadowLg, zIndex: 200, maxHeight: 400, overflowY: "auto" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
                  <p style={{ fontSize: 14, fontWeight: 800, color: T.text }}>Notifications</p>
                </div>
                {(notifications || []).length === 0
                  ? <div style={{ padding: "20px 16px", textAlign: "center" }}><p style={{ fontSize: 13, color: T.muted }}>No notifications yet</p></div>
                  : (notifications || []).map((n, i) => (
                    <div key={i} style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, background: n.read ? "transparent" : T.accentGlow }}>
                      <p style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: T.text, lineHeight: 1.5 }}>{n.message}</p>
                      <p style={{ fontSize: 11, color: T.muted, fontWeight: 500, marginTop: 4 }}>{timeAgo(n.created_at)}</p>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        )}
        {onHome && (
          <button onTouchEnd={(e) => { e.preventDefault(); onHome(); }} onClick={onHome} style={{ minWidth: 44, minHeight: 44, borderRadius: 8, background: T.surface2, border: `1.5px solid ${T.border}`, cursor: "pointer", fontSize: 12, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px", WebkitTapHighlightColor: "transparent" }}>Home</button>
        )}
        {onLogout && (
          <button onClick={onLogout} style={{ background: "none", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: T.muted, cursor: "pointer", transition: "all 0.2s" }}>Sign Out</button>
        )}
      </div>
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled, T, style: sx = {} }) {
  const base = {
    width: "100%", padding: "14px 20px", borderRadius: 12,
    fontSize: 14, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    border: "none", transition: "all 0.2s", opacity: disabled ? 0.5 : 1,
    letterSpacing: 0.2, ...sx
  };
  const v = {
    primary: { background: `linear-gradient(135deg,${T.accent},${T.accentLight})`, color: "#fff", boxShadow: `0 4px 16px ${T.accentGlow}` },
    secondary: { background: T.surface2, color: T.text, border: `1.5px solid ${T.border}` },
    green: { background: `linear-gradient(135deg,${T.green},#10b981)`, color: "#fff", boxShadow: `0 4px 16px rgba(5,150,105,0.25)` },
    gold: { background: `linear-gradient(135deg,${T.gold},${T.goldLight})`, color: "#fff", boxShadow: `0 4px 16px ${T.goldGlow}` },
    ghost: { background: "transparent", color: T.accent, border: `1.5px solid ${T.accent}` },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...v[variant] }}>{children}</button>;
}

function Field({ label, icon, error, hint, T, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</label>}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        background: T.surface2, border: `1.5px solid ${error ? T.red : T.border}`,
        borderRadius: 10, padding: "0 14px", transition: "border-color 0.2s",
      }}>
        {icon && <span style={{ fontSize: 16, opacity: 0.5, flexShrink: 0 }}>{icon}</span>}
        {children}
      </div>
      {error && <p style={{ fontSize: 11, color: T.red, fontWeight: 600, marginTop: 4 }}> {error}</p>}
      {hint && !error && <p style={{ fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 4 }}>{hint}</p>}
    </div>
  );
}

function iS(T) {
  return { border: "none", background: "transparent", width: "100%", padding: "13px 0", fontSize: 14, fontWeight: 500, color: T.text, outline: "none" };
}

function Card({ children, T, style: sx = {}, className = "fu" }) {
  return (
    <div className={className} style={{
      background: T.card, borderRadius: 16, padding: 22,
      boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, ...sx
    }}>
      {children}
    </div>
  );
}

function Badge({ children, color, T }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: color + "20", color,
      border: `1px solid ${color}40`,
      borderRadius: 6, padding: "3px 10px",
      fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    }}>{children}</span>
  );
}

function ProgressBar({ step, steps, T }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        {steps.map((s, i) => (
          <div key={s} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              background: i < step ? T.green : i === step ? T.accent : T.surface2,
              border: `2px solid ${i < step ? T.green : i === step ? T.accent : T.border}`,
              color: i <= step ? "#fff" : T.muted,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 800, transition: "all 0.3s",
              boxShadow: i === step ? `0 0 0 4px ${T.accentGlow}` : "none",
            }}>
              {i < step ? "" : i + 1}
            </div>
            <span style={{ fontSize: 9, fontWeight: 700, color: i === step ? T.accent : T.muted, marginTop: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>{s}</span>
          </div>
        ))}
      </div>
      <div style={{ height: 2, background: T.border, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 2, background: `linear-gradient(90deg,${T.accent},${T.gold})`, width: `${(step / (steps.length - 1)) * 100}%`, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
}

function Spinner({ T, text = "Loading…" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 48, gap: 12 }}>
      <div className="spin" style={{ width: 32, height: 32, border: `3px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%" }} />
      <p style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>{text}</p>
    </div>
  );
}

function EmptyState({ icon, title, sub, action, T }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px" }}>
      <div style={{ fontSize: 52, marginBottom: 14 }}>{icon}</div>
      <p style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 8 }}>{title}</p>
      <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: action ? 24 : 0, lineHeight: 1.6 }}>{sub}</p>
      {action}
    </div>
  );
}

function StatCard({ icon, value, label, T, color }) {
  return (
    <div style={{
      background: T.surface2, borderRadius: 12, padding: "16px 12px",
      textAlign: "center", border: `1px solid ${T.border}`,
    }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || T.accent, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, marginTop: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   WELCOME SCREEN
───────────────────────────────────────────── */
function WelcomeScreen({ T, dark, onToggleTheme, onSelect, onLogin, onDashboard, onLogoTap, onShowTerms, onShowPrivacy }) {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text }}>
      {/* Nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", position: "absolute", top: 0, left: 0, right: 0, zIndex: 10 }}>
        <span onClick={onLogoTap} style={{ cursor: "default", userSelect: "none" }}><Logo T={T} size={20} /></span>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <ThemeToggle dark={dark} onToggle={onToggleTheme} T={T} />
          {onDashboard
            ? <button onClick={onDashboard} style={{ background: "none", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700, color: T.text, cursor: "pointer" }}>My Dashboard</button>
            : <button onClick={onLogin} style={{ background: "none", border: `1.5px solid ${T.border}`, borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700, color: T.text, cursor: "pointer" }}>Sign In</button>
          }
        </div>
      </div>

      {/* Hero */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        {/* Background image */}
        <div style={{ position: "relative", height: 420, overflow: "hidden" }}>
          <img src={UNSPLASH.hero} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: dark ? "linear-gradient(to bottom, rgba(13,17,23,0.4) 0%, rgba(13,17,23,0.95) 100%)" : "linear-gradient(to bottom, rgba(15,23,42,0.3) 0%, rgba(15,23,42,0.9) 100%)" }} />

          {/* Hero text over image */}
          <div className="fu" style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "0 24px 32px", paddingTop: "80px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)", borderRadius: 50, padding: "6px 14px", marginBottom: 16, border: "1px solid rgba(255,255,255,0.15)" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#3fb950", animation: "shimmer 2s infinite" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>Now launching in Marysville, OH</span>
            </div>
            <h1 style={{ fontSize: "clamp(28px,6vw,40px)", fontWeight: 800, color: "#fff", lineHeight: 1.1, letterSpacing: -0.5, marginBottom: 12, textAlign: "center" }}>
              The Safer &<br />
              <span style={{ color: T.goldLight }}>Smarter Way</span><br />
              to Hire.
            </h1>
            <p style={{ fontSize: 15, fontWeight: 500, color: "rgba(255,255,255,0.75)", lineHeight: 1.6, maxWidth: 340, textAlign: "center" }}>
              The easiest way to connect homeowners with trusted local pros.
            </p>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#f0b429", textAlign: "center", marginTop: 10, letterSpacing: 0.5 }}>
              Handyman · Power Washing · Painting · Junk Removal
            </p>
          </div>
        </div>
      </div>

      {/* Role selection */}
      <div className="fu" style={{ padding: "28px 20px 0" }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: T.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 14, textAlign: "center" }}>Get started as a…</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Homeowner card */}
          <button onClick={() => onSelect("homeowner", "signup")} style={{
            background: T.card, border: `1.5px solid ${T.cardBorder}`,
            borderRadius: 16, padding: 0, cursor: "pointer", overflow: "hidden",
            boxShadow: T.shadow, textAlign: "center", transition: "transform 0.2s, box-shadow 0.2s",
          }}>
            <div style={{ position: "relative", height: 120, overflow: "hidden" }}>
              <img src={UNSPLASH.home} alt="Home" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(15,23,42,0.3) 0%, rgba(15,23,42,0.75) 100%)" }} />
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <p style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Homeowner</p>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>Post a job, get bids, pay safely</p>
              </div>
            </div>
          </button>

          {/* Contractor card */}
          <button onClick={() => onSelect("contractor", "signup")} style={{
            background: T.card, border: `1.5px solid ${T.cardBorder}`,
            borderRadius: 16, padding: 0, cursor: "pointer", overflow: "hidden",
            boxShadow: T.shadow, textAlign: "center", transition: "transform 0.2s",
          }}>
            <div style={{ position: "relative", height: 120, overflow: "hidden", display: "flex" }}>
              <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                <img src={UNSPLASH.tools} alt="Tools" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                <img src={UNSPLASH.powerwash} alt="Power Washing" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(15,23,42,0.2) 0%, rgba(15,23,42,0.8) 100%)" }} />
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <p style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Service Pro</p>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>Browse jobs, place bids, get hired</p>
              </div>
            </div>
          </button>
        </div>

        {/* How it works strip */}
        <div style={{ margin: "40px 0 0", background: T.surface2, borderRadius: 16, padding: "20px 18px", border: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: T.muted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 16 }}>How It Works</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              ["1", "Snap a Photo", "Homeowner takes a photo of the job and posts it"],
              ["2", "Get Real Bids", "Verified local pros see it and send their price"],
              ["3", "Get Hired", "Homeowner picks the best bid and contacts the pro directly"],
            ].map(([num, title, desc]) => (
              <div key={title} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff", flexShrink: 0 }}>{num}</div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{title}</p>
                  <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, lineHeight: 1.5 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: "0 20px 32px", textAlign: "center" }}>
          <p style={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>
            <span onClick={() => onShowTerms && onShowTerms()} style={{ color: T.accent, cursor: "pointer", textDecoration: "underline" }}>Terms of Service</span>
            {" · "}
            <span onClick={() => onShowPrivacy && onShowPrivacy()} style={{ color: T.accent, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>
          </p>
          <p style={{ fontSize: 11, color: T.muted, fontWeight: 500, marginTop: 4 }}>Must be 18 or older to use</p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   LOGIN
───────────────────────────────────────────── */
function LoginScreen({ T, dark, onToggleTheme, onBack, onLogin }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [newPw, setNewPw] = useState("");

  const handle = async () => {
    setLoading(true); setErr("");
    try {
      const rows = await sb.select("users", `?email=eq.${encodeURIComponent(email)}&select=*`);
      if (!rows.length) { setErr("No account found with that email."); setLoading(false); return; }
      const hashed = await hashPassword(pw);
      const matched = rows.filter(r => r.password === hashed);
      if (!matched.length) { setErr("Incorrect password."); setLoading(false); return; }
      if (rows.length > 1) { setAccounts(matched.length > 1 ? matched : rows.filter(r => matched.map(m => m.email).includes(r.email))); }
      else { onLogin(matched[0]); }
    } catch { setErr("Connection error. Please try again."); }
    setLoading(false);
  };

  if (accounts) return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <Topbar T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={onBack} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 20px" }}>
        <div className="fu" style={{ width: "100%", maxWidth: 400 }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <Logo T={T} size={22} />
            <p style={{ fontSize: 14, color: T.muted, fontWeight: 500, marginTop: 8 }}>Welcome back</p>
          </div>
          <Card T={T}>
            <p style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 16 }}>Which account would you like to use?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              {accounts.map(a => (
                <button key={a.id} onClick={() => onLogin(a)} style={{ padding: "16px 20px", borderRadius: 12, border: `1.5px solid ${T.border}`, background: T.surface2, cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{a.role === "contractor" ? "Service Pro" : "Homeowner"}</p>
                    <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginTop: 2 }}>{a.first_name} {a.last_name}</p>
                  </div>
                  <span style={{ fontSize: 18, color: T.muted }}>›</span>
                </button>
              ))}
            </div>
            <Btn variant="secondary" onClick={() => setAccounts(null)} T={T}>← Back</Btn>
          </Card>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <Topbar T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={onBack} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 20px" }}>
        <div className="fu" style={{ width: "100%", maxWidth: 400 }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <Logo T={T} size={22} />
            <p style={{ fontSize: 14, color: T.muted, fontWeight: 500, marginTop: 8 }}>Welcome back</p>
          </div>
          <Card T={T}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 20 }}>Sign In</h2>
            <Field label="Email" icon="" T={T}><input style={iS(T)} type="email" placeholder="" value={email} onChange={e => setEmail(e.target.value)} /></Field>
            <Field label="Password" icon="" T={T}><input style={iS(T)} type="password" placeholder="" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && handle()} /></Field>
            {err && <p style={{ fontSize: 12, color: T.red, fontWeight: 600, marginBottom: 12 }}>{err}</p>}
            {forgotMode ? (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 10, lineHeight: 1.6 }}>Enter your email and new password below.</p>
                <Field label="New Password" icon="" T={T}><input style={iS(T)} type="password" placeholder="Min 6 characters" value={newPw} onChange={e => setNewPw(e.target.value)} /></Field>
                <Btn onClick={async () => {
                  if (!email || newPw.length < 6) return;
                  const rows = await sb.select("users", `?email=eq.${encodeURIComponent(email)}`);
                  if (!rows.length) { setErr("No account found with that email."); return; }
                  const hashed = await hashPassword(newPw);
                  await Promise.all(rows.map(r => sb.update("users", { password: hashed }, `?id=eq.${r.id}`)));
                  setForgotMode(false); setNewPw(""); setErr(""); alert("Password updated. Please sign in.");
                }} T={T}>Update Password</Btn>
                <Btn variant="secondary" onClick={() => setForgotMode(false)} T={T} style={{ marginTop: 8 }}>Back to Sign In</Btn>
              </div>
            ) : (
              <>
                <Btn onClick={handle} disabled={loading} T={T}>{loading ? <div className="spin" style={{ width: 18, height: 18, border: `2px solid rgba(255,255,255,0.3)`, borderTopColor: "#fff", borderRadius: "50%", margin: "0 auto" }} /> : "Sign In"}</Btn>
                <p onClick={() => setForgotMode(true)} style={{ textAlign: "center", fontSize: 12, color: T.accent, fontWeight: 600, marginTop: 12, cursor: "pointer", textDecoration: "underline" }}>Forgot Password?</p>
              </>
            )}
            <Btn variant="secondary" onClick={onBack} T={T} style={{ marginTop: 10 }}>← Back</Btn>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CONTRACTOR SIGNUP
───────────────────────────────────────────── */
function ContractorSignup({ T, dark, onToggleTheme, onDone, onLogin, onBack }) {
  const [step, setStep] = useState(0);
  const [d, setD] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const u = (k, v) => setD(p => ({ ...p, [k]: v }));

  const validate0 = () => {
    const e = {};
    if (!d.firstName) e.firstName = "Required";
    if (!d.lastName) e.lastName = "Required";
    if (!d.email || !/\S+@\S+\.\S+/.test(d.email)) e.email = "Valid email required";
    if (!d.phone || d.phone.replace(/\D/g, "").length < 10) e.phone = "10-digit phone required";
    if (!d.password || d.password.length < 6) e.password = "Min 6 characters";
    setErrors(e); return !Object.keys(e).length;
  };

  const save = async () => {
    setLoading(true); setErr("");
    try {
      const exists = await sb.select("users", `?email=eq.${encodeURIComponent(d.email)}&role=eq.contractor`);
      if (exists.length) { setErr("An account with this email already exists."); setLoading(false); return; }
      const hashed = await hashPassword(d.password);
      const rows = await sb.insert("users", {
        email: d.email, password: hashed, role: "contractor",
        first_name: d.firstName, last_name: d.lastName, phone: d.phone,
        business_name: d.businessName || `${d.firstName} ${d.lastName}`,
        city: d.city, bio: d.bio, trades: JSON.stringify(d.trades || ["handyman"]), radius: d.radius,
        insurance: null, license: null, id_doc: null, verified: false,
      });
      if (rows && rows[0]) { session.set(rows[0]); onDone(rows[0]); }
    } catch (e) { setErr("Error creating account. Please try again."); }
    setLoading(false);
  };

  const screens = [
    // Account
    <div className="fu" key="c0">
      <h2 style={{ fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 20 }}>Create Account</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="First Name" icon="" error={errors.firstName} T={T}><input style={iS(T)} placeholder="" value={d.firstName || ""} onChange={e => u("firstName", e.target.value)} /></Field>
        <Field label="Last Name" icon="" error={errors.lastName} T={T}><input style={iS(T)} placeholder="" value={d.lastName || ""} onChange={e => u("lastName", e.target.value)} /></Field>
      </div>
      <Field label="Email" icon="" error={errors.email} T={T}><input style={iS(T)} type="email" placeholder="" value={d.email || ""} onChange={e => u("email", e.target.value)} /></Field>
      <Field label="Phone (10 digits)" icon="" error={errors.phone} T={T}><input style={iS(T)} type="tel" placeholder="" value={d.phone || ""} onChange={e => u("phone", e.target.value)} /></Field>
      <Field label="Password" icon="" error={errors.password} T={T}><input style={iS(T)} type="password" placeholder="Min 6 characters" value={d.password || ""} onChange={e => u("password", e.target.value)} /></Field>
      <Btn onClick={() => validate0() && setStep(1)} T={T}>Continue →</Btn>
      <div style={{ textAlign: "center", marginTop: 14 }}>
        <button onClick={onLogin} style={{ background: "none", border: "none", fontSize: 13, fontWeight: 700, color: T.accent, cursor: "pointer" }}>Already have an account? Sign in</button>
      </div>
    </div>,

    // Profile
    <div className="fu" key="c1">
      <h2 style={{ fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 4 }}>Your Profile</h2>
      <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 18 }}>What homeowners will see when you bid.</p>
      <Field label="Business or Your Name" icon="" T={T}><input style={iS(T)} placeholder="" value={d.businessName || ""} onChange={e => u("businessName", e.target.value)} /></Field>
      <Field label="City & State" icon="" T={T}><input style={iS(T)} placeholder="" value={d.city || ""} onChange={e => u("city", e.target.value)} /></Field>
      <Field label="How far will you travel?" icon="" T={T}>
        <select style={{ ...iS(T), cursor: "pointer" }} value={d.radius || ""} onChange={e => u("radius", e.target.value)}>
          <option value="" style={{ background: "#1e293b", color: "#fff" }}>Select radius…</option>
          {["Within 10 miles", "Within 25 miles", "Within 50 miles"].map(r => <option key={r} style={{ background: "#1e293b", color: "#fff" }}>{r}</option>)}
        </select>
      </Field>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 10, letterSpacing: 0.4, textTransform: "uppercase" }}>What services do you offer?</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {CATEGORIES.map(cat => {
            const selected = (d.trades || []).includes(cat.id);
            return (
              <div key={cat.id} onClick={() => {
                const current = d.trades || [];
                u("trades", selected ? current.filter(t => t !== cat.id) : [...current, cat.id]);
              }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${selected ? cat.color : T.border}`, background: selected ? cat.color + "15" : T.surface2, cursor: "pointer" }}>
                
                <span style={{ fontSize: 14, fontWeight: 700, color: selected ? cat.color : T.text }}>{cat.label}</span>
                <span style={{ marginLeft: "auto", width: 20, height: 20, borderRadius: "50%", border: `2px solid ${selected ? cat.color : T.border}`, background: selected ? cat.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff" }}>{selected ? "✓" : ""}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>About Your Work</label>
        <textarea placeholder="Describe what you do, what you specialize in, why homeowners should hire you…" value={d.bio || ""} onChange={e => u("bio", e.target.value)} style={{ width: "100%", minHeight: 90, padding: "12px 14px", background: T.surface2, border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: 13, fontWeight: 500, color: T.text, outline: "none", lineHeight: 1.6 }} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn variant="secondary" onClick={() => setStep(0)} T={T} style={{ width: 48, padding: "14px 0", flexShrink: 0 }}>←</Btn>
        <Btn onClick={() => d.city && (d.trades || []).length > 0 && setStep(2)} disabled={!d.city || !(d.trades || []).length} T={T}>Continue</Btn>
      </div>
    </div>,

    // Verify
    <div className="fu" key="c2">
      <h2 style={{ fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 4 }}>Get Verified</h2>
      <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 16 }}>Verified pros get the most jobs and appear first.</p>

      <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", marginBottom: 18 }}>
        
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", padding: "0 20px", gap: 14 }}>
          <div style={{ fontSize: 32 }}></div>
          <div>
            <p style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>Verified Pro Badge</p>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>Appears on your profile in search results</p>
          </div>
        </div>
      </div>

      <div style={{ background: T.surface2, borderRadius: 14, padding: 18, marginBottom: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}> What to send us:</p>
        {[
          ["", "Insurance Certificate (COI)", "Photo of your general liability insurance"],
          ["🪪", "Government ID", "Photo of your driver's license or state ID"],
          ["", "License (if applicable)", "Ohio license if your work requires it"],
        ].map(([ic, title, desc]) => (
          <div key={title} style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-start" }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: T.accentGlow, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{ic}</div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{title}</p>
              <p style={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>{desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: T.greenBg, border: `1.5px solid ${T.greenBorder}`, borderRadius: 12, padding: "16px 18px", marginBottom: 16, textAlign: "center" }}>
        <p style={{ fontSize: 22, marginBottom: 8 }}></p>
        <p style={{ fontSize: 14, fontWeight: 800, color: T.green, marginBottom: 8 }}>Email your photos directly to us</p>
        <a href={`mailto:kyle@usehirehero.com?subject=HireHero Verification - ${d.firstName || ""} ${d.lastName || ""}`} style={{ display: "block", background: T.accent, borderRadius: 10, padding: "14px", border: "none", fontSize: 15, fontWeight: 800, color: "#fff", textDecoration: "none", marginBottom: 8, textAlign: "center" }}>
          Send Verification Docs
        </a>
        <p style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>Tap above — your name is pre-filled in the subject line</p>
      </div>

      <div style={{ background: T.goldGlow, border: `1px solid ${T.gold}40`, borderRadius: 10, padding: "11px 14px", marginBottom: 18 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: T.gold }}>⏱ Reviewed within 1–2 business days. Browse and bid on jobs right away while you wait.</p>
      </div>

      {err && <p style={{ fontSize: 12, color: T.red, fontWeight: 600, marginBottom: 12 }}>{err}</p>}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, padding: "12px 14px", background: T.surface2, borderRadius: 10 }}>
        <input type="checkbox" id="contractor-terms" checked={termsAgreed} onChange={e => setTermsAgreed(e.target.checked)} style={{ marginTop: 2, cursor: "pointer", width: 16, height: 16, flexShrink: 0 }} />
        <label htmlFor="contractor-terms" style={{ fontSize: 12, color: T.muted, fontWeight: 500, lineHeight: 1.6, cursor: "pointer" }}>
          I agree to the <span onClick={() => setShowTerms(true)} style={{ color: T.accent, fontWeight: 700, textDecoration: "underline" }}>Terms of Service</span> and <span onClick={() => setShowPrivacy(true)} style={{ color: T.accent, fontWeight: 700, textDecoration: "underline" }}>Privacy Policy</span>. I am 18 or older.
        </label>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn variant="secondary" onClick={() => setStep(1)} T={T} style={{ width: 48, padding: "14px 0", flexShrink: 0 }}>←</Btn>
        <Btn onClick={() => !loading && termsAgreed && save()} disabled={loading || !termsAgreed} T={T}>
          {loading ? <div className="spin" style={{ width: 18, height: 18, border: `2px solid rgba(255,255,255,0.3)`, borderTopColor: "#fff", borderRadius: "50%", margin: "0 auto" }} /> : "Submit & Get Started"}
        </Btn>
      </div>
    </div>,

    // Done
    <div className="fu" key="c3" style={{ textAlign: "center" }}>
      <div className="pop" style={{ fontSize: 64, marginBottom: 16 }}></div>
      <h2 style={{ fontSize: 30, fontWeight: 800, color: T.text, marginBottom: 10 }}>You're In, {d.firstName}!</h2>
      <p style={{ fontSize: 14, color: T.muted, fontWeight: 500, lineHeight: 1.7, marginBottom: 24 }}>
        Profile submitted. Send your verification photos to our team and we'll activate your badge within 1–2 days.
      </p>
      <div style={{ background: T.surface2, borderRadius: 14, padding: 18, marginBottom: 24, textAlign: "left" }}>
        {[["", "Confirmation sent", `Check ${d.email}`], ["", "We review your docs", "1–2 business days"], ["", "Verified Pro Badge", "Added to your profile"], ["", "Jobs start coming in", "From homeowners near you"]].map(([ic, title, sub]) => (
          <div key={title} style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{ic}</span>
            <div><p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{title}</p><p style={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>{sub}</p></div>
          </div>
        ))}
      </div>
      <Btn onClick={() => onDone(session.get())} T={T}>Go to Dashboard</Btn>
    </div>,
  ];

  if (showTerms) return <TermsScreen T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={() => setShowTerms(false)} />;
  if (showPrivacy) return <PrivacyScreen T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={() => setShowPrivacy(false)} />;

  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <Topbar T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={step > 0 ? () => setStep(s => s - 1) : onBack} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 0 48px" }}>
        <div style={{ width: "100%", maxWidth: 440, padding: "0 16px" }}>
          <div style={{ marginBottom: 20 }}><ProgressBar step={step} steps={C_STEPS} T={T} /></div>
          <Card T={T}>{screens[step]}</Card>
          <p style={{ textAlign: "center", fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 14 }}>Encrypted & secure · usehirehero.com</p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   HOMEOWNER SIGNUP
───────────────────────────────────────────── */
function HomeownerSignup({ T, dark, onToggleTheme, onDone, onLogin, onBack }) {
  const [step, setStep] = useState(0);
  const [d, setD] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const validate0 = () => {
    const e = {};
    if (!d.firstName) e.firstName = "Required";
    if (!d.lastName) e.lastName = "Required";
    if (!d.email || !/\S+@\S+\.\S+/.test(d.email)) e.email = "Valid email required";
    if (!d.phone || d.phone.replace(/\D/g, "").length < 10) e.phone = "10-digit phone required";
    if (!d.password || d.password.length < 6) e.password = "Min 6 characters";
    setErrors(e); return !Object.keys(e).length;
  };

  const save = async () => {
    setLoading(true); setErr("");
    try {
      const exists = await sb.select("users", `?email=eq.${encodeURIComponent(d.email)}&role=eq.contractor`);
      if (exists.length) { setErr("An account with this email already exists."); setLoading(false); return; }
      const hashed = await hashPassword(d.password);
      const rows = await sb.insert("users", {
        email: d.email, password: hashed, role: "homeowner",
        first_name: d.firstName, last_name: d.lastName,
        phone: d.phone, city: d.city, address: d.address, zip: d.zip,
      });
      if (rows && rows[0]) { session.set(rows[0]); onDone(rows[0]); }
    } catch { setErr("Error creating account. Please try again."); }
    setLoading(false);
  };

  const screens = [
    <div className="fu" key="h0">
      <h2 style={{ fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 4 }}>Create Account</h2>
      <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 18 }}>Free to join. Post your first job in minutes.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="First Name" icon="" error={errors.firstName} T={T}><input style={iS(T)} placeholder="" value={d.firstName || ""} onChange={e => u("firstName", e.target.value)} /></Field>
        <Field label="Last Name" icon="" error={errors.lastName} T={T}><input style={iS(T)} placeholder="" value={d.lastName || ""} onChange={e => u("lastName", e.target.value)} /></Field>
      </div>
      <Field label="Email" icon="" error={errors.email} T={T}><input style={iS(T)} type="email" placeholder="" value={d.email || ""} onChange={e => u("email", e.target.value)} /></Field>
      <Field label="Phone (10 digits)" icon="" error={errors.phone} T={T}><input style={iS(T)} type="tel" placeholder="" value={d.phone || ""} onChange={e => u("phone", e.target.value)} /></Field>
      <Field label="Password" icon="" error={errors.password} T={T}><input style={iS(T)} type="password" placeholder="Min 6 characters" value={d.password || ""} onChange={e => u("password", e.target.value)} /></Field>
      <Btn onClick={() => validate0() && setStep(1)} T={T}>Continue →</Btn>
      <div style={{ textAlign: "center", marginTop: 14 }}>
        <button onClick={onLogin} style={{ background: "none", border: "none", fontSize: 13, fontWeight: 700, color: T.accent, cursor: "pointer" }}>Already have an account? Sign in</button>
      </div>
    </div>,

    <div className="fu" key="h1">
      <h2 style={{ fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 4 }}>Your Home</h2>
      <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 18 }}>Helps us match nearby handymen to you.</p>
      <Field label="Street Address" icon="" T={T}><input style={iS(T)} placeholder="" value={d.address || ""} onChange={e => u("address", e.target.value)} /></Field>
      <Field label="City & State" icon="" T={T}><input style={iS(T)} placeholder="" value={d.city || ""} onChange={e => u("city", e.target.value)} /></Field>
      <Field label="Zip Code" icon="" T={T}><input style={iS(T)} placeholder="" value={d.zip || ""} onChange={e => u("zip", e.target.value)} /></Field>
      {err && <p style={{ fontSize: 12, color: T.red, fontWeight: 600, marginBottom: 12 }}>{err}</p>}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14, padding: "12px 14px", background: T.surface2, borderRadius: 10 }}>
        <input type="checkbox" id="homeowner-terms" checked={termsAgreed} onChange={e => setTermsAgreed(e.target.checked)} style={{ marginTop: 2, cursor: "pointer", width: 16, height: 16, flexShrink: 0 }} />
        <label htmlFor="homeowner-terms" style={{ fontSize: 12, color: T.muted, fontWeight: 500, lineHeight: 1.6, cursor: "pointer" }}>
          I agree to the <span onClick={() => setShowTerms(true)} style={{ color: T.accent, fontWeight: 700, textDecoration: "underline" }}>Terms of Service</span> and <span onClick={() => setShowPrivacy(true)} style={{ color: T.accent, fontWeight: 700, textDecoration: "underline" }}>Privacy Policy</span>. I am 18 or older.
        </label>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn variant="secondary" onClick={() => setStep(0)} T={T} style={{ width: 48, padding: "14px 0", flexShrink: 0 }}>←</Btn>
        <Btn onClick={() => d.city && termsAgreed && save()} disabled={!d.city || !termsAgreed || loading} T={T}>
          {loading ? <div className="spin" style={{ width: 18, height: 18, border: `2px solid rgba(255,255,255,0.3)`, borderTopColor: "#fff", borderRadius: "50%", margin: "0 auto" }} /> : "Create Account"}
        </Btn>
      </div>
    </div>,

    <div className="fu" key="h2" style={{ textAlign: "center" }}>
      <div className="pop" style={{ fontSize: 64, marginBottom: 16 }}></div>
      <h2 style={{ fontSize: 30, fontWeight: 800, color: T.text, marginBottom: 10 }}>Welcome, {d.firstName}!</h2>
      <p style={{ fontSize: 14, color: T.muted, fontWeight: 500, lineHeight: 1.7, marginBottom: 24 }}>Your account is ready. Post your first job now and get bids fast.</p>
      <Btn onClick={() => onDone(session.get())} T={T}>Post My First Job →</Btn>
    </div>,
  ];

  if (showTerms) return <TermsScreen T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={() => setShowTerms(false)} />;
  if (showPrivacy) return <PrivacyScreen T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={() => setShowPrivacy(false)} />;

  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <Topbar T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={step > 0 ? () => setStep(s => s - 1) : onBack} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 0 48px" }}>
        <div style={{ width: "100%", maxWidth: 440, padding: "0 16px" }}>
          <div style={{ marginBottom: 20 }}><ProgressBar step={step} steps={H_STEPS} T={T} /></div>
          <Card T={T}>{screens[step]}</Card>
          <p style={{ textAlign: "center", fontSize: 11, color: T.muted, fontWeight: 600, marginTop: 14 }}>Encrypted & secure · usehirehero.com</p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CONTRACTOR DASHBOARD
───────────────────────────────────────────── */
function ContractorDashboard({ T, dark, onToggleTheme, user, onLogout, onHome, onSwitch }) {
  const [tab, setTab] = useState("jobs");
  const [askingJob, setAskingJob] = useState(null);
  const [questionText, setQuestionText] = useState("");
  const [editingProfile, setEditingProfile] = useState(false);
  const [eData, setEData] = useState({});
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const eu = (k, v) => setEData(p => ({ ...p, [k]: v }));

  const startEdit = () => {
    setEData({ firstName: user.first_name, lastName: user.last_name, businessName: user.business_name, city: user.city, phone: user.phone, bio: user.bio, email: user.email, newPassword: "", confirmPassword: "", trades: user.trades ? JSON.parse(user.trades) : [] });
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    if (eData.newPassword && eData.newPassword !== eData.confirmPassword) {
      alert("Passwords don't match"); return;
    }
    if (eData.newPassword && eData.newPassword.length < 6) {
      alert("Password must be at least 6 characters"); return;
    }
    setSaving(true);
    try {
      const updates = {
        first_name: eData.firstName, last_name: eData.lastName,
        business_name: eData.businessName, city: eData.city,
        phone: eData.phone, bio: eData.bio, email: eData.email,
        trades: JSON.stringify(eData.trades || (user.trades ? JSON.parse(user.trades) : [])),
      };
      if (eData.newPassword) {
        updates.password = await hashPassword(eData.newPassword);
      }
      await sb.update("users", updates, `?id=eq.${user.id}`);
      session.set({ ...user, ...updates, first_name: eData.firstName, last_name: eData.lastName });
      setEditingProfile(false);
      window.location.reload();
    } catch { }
    setSaving(false);
  };
  const [jobs, setJobs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [bidNote, setBidNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const verified = user.verified === true || user.verified === "true";

  const load = async () => {
    setLoading(true);
    try {
      const [j, b, n, freshUser] = await Promise.all([
        sb.select("jobs", "?status=eq.open&order=created_at.desc"),
        sb.select("bids", `?contractor_id=eq.${user.id}&order=created_at.desc`),
        sb.select("notifications", `?user_id=eq.${user.id}&order=created_at.desc`),
        sb.select("users", `?id=eq.${user.id}`),
      ]);
      const contractorTrades = freshUser?.[0]?.trades ? JSON.parse(freshUser[0].trades) : ["handyman"];
      const filteredJobs = (j || []).filter(job => !job.trade || contractorTrades.includes(job.trade) || contractorTrades.length === 0);
      setJobs(filteredJobs); setMyBids(b || []); setNotifications(n || []);
      if (freshUser && freshUser[0]) session.set({ ...user, ...freshUser[0] });
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [tab]);

  useEffect(() => {
    const interval = setInterval(() => load(), 30000);
    return () => clearInterval(interval);
  }, []);

  const submitBid = async () => {
    if (!bidAmount) return;
    setSubmitting(true);
    try {
      await sb.insert("bids", {
        job_id: selectedJob.id, contractor_id: user.id,
        contractor_name: user.business_name || `${user.first_name} ${user.last_name}`,
        contractor_verified: verified, amount: parseFloat(bidAmount), note: bidNote, status: "pending",
      });
      await sb.update("jobs", { bid_count: (selectedJob.bid_count || 0) + 1 }, `?id=eq.${selectedJob.id}`);
      // Notify homeowner
      await sb.insert("notifications", {
        user_id: selectedJob.homeowner_id,
        message: `${user.business_name || user.first_name} placed a $${bidAmount} bid on your job "${selectedJob.title}"`,
        type: "bid", read: false,
      });
      setSelectedJob(null); setBidAmount(""); setBidNote(""); setTab("bids"); load();
    } catch {}
    setSubmitting(false);
  };

  const alreadyBid = selectedJob && myBids.some(b => b.job_id === selectedJob.id);
  const wonBids = myBids.filter(b => b.status === "accepted");

  const mockJobImages = [UNSPLASH.job1, UNSPLASH.job2, UNSPLASH.job3];

  return (
    <div style={{ background: T.bg, minHeight: "100vh", paddingBottom: 80 }}>
      <Topbar T={T} user={user} onLogout={onLogout} dark={dark} onToggleTheme={onToggleTheme} onHome={onHome} onSwitch={onSwitch} notifications={notifications} onNotifClick={async () => { await Promise.all(notifications.filter(n => !n.read).map(n => sb.update("notifications", { read: true }, `?id=eq.${n.id}`))); load(); }} />

      {/* Hero header */}
      <div style={{ position: "relative", margin: "16px 16px 0", borderRadius: 18, overflow: "hidden" }}>
        
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg,${T.accent}cc,${T.blueDeep || "#0d2a6e"}cc)`, opacity: 0.7 }} />
        <div style={{ position: "absolute", inset: 0, padding: "20px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.65)", marginBottom: 4, letterSpacing: 0.5 }}>Welcome back </p>
              <p style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 10 }}>{user.business_name || `${user.first_name} ${user.last_name}`}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ background: (verified ? "#3fb950" : T.gold) + "30", border: `1px solid ${verified ? "#3fb950" : T.gold}60`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: verified ? "#3fb950" : T.gold }}>
                  {verified ? " Verified Pro" : "⏳ Pending Verification"}
                </span>
                <span style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>
                   {user.city || "Ohio"}
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 14 }}>
            {[["", jobs.length, "Open Jobs"], ["", myBids.length, "My Bids"], ["", wonBids.length, "Won"]].map(([ic, v, l]) => (
              <div key={l} style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)", borderRadius: 10, padding: "10px 8px", textAlign: "center", border: "1px solid rgba(255,255,255,0.15)" }}>
                <div style={{ fontSize: 16 }}>{ic}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#fff" }}>{v}</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 0.5, textTransform: "uppercase" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!verified && (
        <div style={{ margin: "12px 16px 0", background: T.goldGlow, border: `1.5px solid ${T.gold}50`, borderRadius: 12, padding: "12px 16px", display: "flex", gap: 10 }}>
          <span style={{ fontSize: 18 }}></span>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.gold }}>Verification in progress</p>
            <p style={{ fontSize: 11, fontWeight: 500, color: T.muted }}>Tap below to send your docs · Approved in 1–2 days</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", margin: "14px 16px 0", background: T.surface, borderRadius: 12, padding: 4, border: `1px solid ${T.border}` }}>
        {[["jobs", " Jobs"], ["bids", " Bids"], ["profile", " Profile"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "10px 4px", borderRadius: 10, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", background: tab === t ? T.accent : "transparent", color: tab === t ? "#fff" : T.muted, transition: "all 0.2s" }}>{l}</button>
        ))}
      </div>

      <div style={{ padding: "14px 16px 0" }}>
        {loading ? <Spinner T={T} /> : (
          <>
            {tab === "jobs" && (
              <div className="fu">
                {jobs.length === 0
                  ? <EmptyState T={T} icon="" title="No open jobs yet" sub="Check back soon — homeowners post jobs daily in your area." />
                  : jobs.map((job, idx) => {
                    const alreadyBidThis = myBids.some(b => b.job_id === job.id);
                    return (
                      <div key={job.id} style={{ background: T.card, borderRadius: 16, marginBottom: 14, border: `1px solid ${T.cardBorder}`, boxShadow: T.shadow, overflow: "hidden" }}>
                        {job.photo_url && <img src={job.photo_url} alt="" style={{ width: "100%", height: 180, objectFit: "cover" }} />}
                        <div style={{ padding: 16 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                            <p style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{job.title}</p>
                          </div>
                          <p style={{ fontSize: 12, color: T.muted, fontWeight: 600, marginBottom: 8 }}> {job.city} · {timeAgo(job.created_at)}</p>
                          <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, lineHeight: 1.55, marginBottom: 12 }}>{job.description}</p>
                          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                            {(() => { const cat = CATEGORIES.find(c => c.id === job.trade) || CATEGORIES[0]; return <span style={{ background: cat.color + "20", color: cat.color, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{cat.label}</span>; })()}
                            {job.urgency === "urgent" && <span style={{ background: "#dc262620", color: "#dc2626", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>Urgent</span>}
                            <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{job.bid_count || 0} bid{job.bid_count !== 1 ? "s" : ""}</span>
                          </div>
                          {alreadyBidThis
                            ? <div style={{ background: T.greenBg, borderRadius: 10, padding: "10px", textAlign: "center", fontSize: 13, fontWeight: 700, color: T.green }}>You placed a bid on this job</div>
                            : <div style={{ display: "flex", gap: 8 }}>
                                <Btn onClick={() => verified ? setSelectedJob(job) : null} disabled={!verified} variant={verified ? "primary" : "secondary"} T={T} style={{ flex: 2 }}>
                                  {verified ? "Place Bid" : "Verify to Bid"}
                                </Btn>
                                <button onClick={() => setAskingJob(job)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface2, color: T.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Ask ?</button>
                              </div>
                          }
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            )}

            {tab === "bids" && (
              <div className="fu">
                {myBids.length === 0
                  ? <EmptyState T={T} icon="" title="No bids yet" sub="Browse available jobs and place your first bid." action={<Btn onClick={() => setTab("jobs")} T={T} style={{ maxWidth: 200, margin: "0 auto" }}>Browse Jobs</Btn>} />
                  : myBids.map(bid => (
                    <div key={bid.id} style={{ background: T.card, borderRadius: 14, padding: 16, marginBottom: 12, border: `1.5px solid ${bid.status === "accepted" ? T.green : T.cardBorder}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <p style={{ fontSize: 15, fontWeight: 800, color: T.text }}>Bid submitted</p>
                        <Badge color={bid.status === "accepted" ? T.green : bid.status === "declined" ? T.muted : T.gold} T={T}>
                          {bid.status === "accepted" ? " Won" : bid.status === "declined" ? "Not selected" : "⏳ Pending"}
                        </Badge>
                      </div>
                      <p style={{ fontSize: 12, color: T.muted, fontWeight: 600, marginBottom: 8 }}>{timeAgo(bid.created_at)}</p>
                      <span style={{ background: T.accentGlow, color: T.accent, borderRadius: 6, padding: "4px 12px", fontSize: 14, fontWeight: 800 }}>Your bid: ${bid.amount}</span>
                      {bid.note && <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginTop: 10, fontStyle: "italic", lineHeight: 1.5 }}>"{bid.note}"</p>}
                      {bid.status === "accepted" && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10, textAlign: "center" }}>
                            <p style={{ fontSize: 18, fontWeight: 800, color: T.green, marginBottom: 4 }}>Congratulations!</p>
                            <p style={{ fontSize: 13, color: T.muted, fontWeight: 500 }}>You won the bid for ${bid.amount}. Reach out to the homeowner to get started.</p>
                          </div>
                          <a href={`tel:${bid.homeowner_phone}`} style={{ display: "flex", alignItems: "center", gap: 10, background: T.surface2, borderRadius: 10, padding: "10px 12px", textDecoration: "none", border: `1px solid ${T.border}` }}>
                            <div>
                              <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{bid.homeowner_name}</p>
                              <p style={{ fontSize: 12, color: T.accent, fontWeight: 600 }}>{bid.homeowner_phone || "Contact via app"}</p>
                            </div>
                            <span style={{ marginLeft: "auto", fontSize: 20 }}>📞</span>
                          </a>
                          {!bid.job_completed && (
                            <button onClick={async () => {
                              if (!window.confirm("Mark this job as complete? The homeowner will have 72 hours to approve or dispute.")) return;
                              await sb.update("jobs", { status: "pending_approval", completed_at: new Date().toISOString() }, `?id=eq.${bid.job_id}`);
                              await sb.update("bids", { job_completed: true }, `?id=eq.${bid.id}`);
                              await sb.insert("notifications", {
                                user_id: bid.homeowner_id || null,
                                message: `Your job "${bid.job_title || "job"}" has been marked complete. Please approve within 72 hours or open a dispute.`,
                                type: "complete", read: false,
                              });
                              load();
                            }} style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 10, border: "none", background: T.green, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                              Mark Job Complete
                            </button>
                          )}
                          {bid.job_completed && (
                            <div style={{ marginTop: 10, background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                              <p style={{ fontSize: 13, fontWeight: 700, color: T.green }}>Job marked complete. Waiting for homeowner approval.</p>
                              <p style={{ fontSize: 11, color: T.muted, fontWeight: 500, marginTop: 4 }}>Payment releases automatically in 72 hours if no dispute is filed.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                }
              </div>
            )}

            {tab === "profile" && (
              <div className="fu">
                {/* Profile Header */}
                <Card T={T} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                    <div style={{ width: 60, height: 60, borderRadius: "50%", background: T.accentGlow, border: `3px solid ${T.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
                      {user.avatar_url ? <img src={user.avatar_url} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} alt="" /> : user.first_name?.[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{user.business_name || `${user.first_name} ${user.last_name}`}</p>
                      <p style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{user.city}</p>
                      {verified && <span style={{ background: T.green + "20", color: T.green, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, marginTop: 4, display: "inline-block" }}>Verified Pro</span>}
                    </div>
                    <button onClick={() => editingProfile ? setEditingProfile(false) : startEdit()} style={{ background: T.accentGlow, border: `1.5px solid ${T.accent}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: T.accent, cursor: "pointer" }}>
                      {editingProfile ? "Cancel" : "Edit"}
                    </button>
                  </div>

                  {editingProfile && (
                      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "flex", gap: 10 }}>
                          <Field label="First Name" icon="" T={T} style={{ flex: 1 }}><input style={iS(T)} value={eData.firstName || ""} onChange={e => eu("firstName", e.target.value)} /></Field>
                          <Field label="Last Name" icon="" T={T} style={{ flex: 1 }}><input style={iS(T)} value={eData.lastName || ""} onChange={e => eu("lastName", e.target.value)} /></Field>
                        </div>
                        <Field label="Business Name" icon="" T={T}><input style={iS(T)} value={eData.businessName || ""} onChange={e => eu("businessName", e.target.value)} /></Field>
                        <Field label="City" icon="" T={T}><input style={iS(T)} value={eData.city || ""} onChange={e => eu("city", e.target.value)} /></Field>
                        <Field label="Phone" icon="" T={T}><input style={iS(T)} value={eData.phone || ""} onChange={e => eu("phone", e.target.value)} /></Field>
                        <Field label="Email" icon="" T={T}><input style={iS(T)} type="email" value={eData.email || ""} onChange={e => eu("email", e.target.value)} /></Field>
                        <Field label="Bio" icon="" T={T}><textarea style={{ ...iS(T), minHeight: 80 }} value={eData.bio || ""} onChange={e => eu("bio", e.target.value)} /></Field>
                        
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 10, letterSpacing: 0.4, textTransform: "uppercase" }}>Services You Offer</label>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {CATEGORIES.map(cat => {
                              const currentTrades = eData.trades || (user.trades ? JSON.parse(user.trades) : []);
                              const selected = currentTrades.includes(cat.id);
                              return (
                                <div key={cat.id} onClick={() => {
                                  const current = eData.trades || (user.trades ? JSON.parse(user.trades) : []);
                                  eu("trades", selected ? current.filter(t => t !== cat.id) : [...current, cat.id]);
                                }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${selected ? cat.color : T.border}`, background: selected ? cat.color + "15" : T.surface2, cursor: "pointer" }}>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: selected ? cat.color : T.text, flex: 1 }}>{cat.label}</span>
                                  <span style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${selected ? cat.color : T.border}`, background: selected ? cat.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", flexShrink: 0 }}>{selected ? "✓" : ""}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, marginTop: 4 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>Change Password</p>
                          <Field label="New Password" icon="" T={T}><input style={iS(T)} type="password" placeholder="Leave blank to keep current" value={eData.newPassword || ""} onChange={e => eu("newPassword", e.target.value)} /></Field>
                          <Field label="Confirm Password" icon="" T={T}><input style={iS(T)} type="password" placeholder="Confirm new password" value={eData.confirmPassword || ""} onChange={e => eu("confirmPassword", e.target.value)} /></Field>
                        </div>
                        <Btn onClick={saveProfile} disabled={saving} T={T}>{saving ? "Saving..." : "Save Changes"}</Btn>
                      </div>
                  )}

                  {user.bio && <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, lineHeight: 1.6, marginBottom: 14, padding: "12px 14px", background: T.surface2, borderRadius: 10 }}>{user.bio}</p>}
                </Card>

                {/* Past Work Photos */}
                <Card T={T} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <p style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Past Work</p>
                    <div onClick={() => document.getElementById("work-photo-upload").click()} style={{ background: T.accentGlow, border: `1.5px solid ${T.accent}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: T.accent, cursor: "pointer" }}>+ Add Photo</div>
                    <input id="work-photo-upload" type="file" accept="image/*" multiple style={{ display: "none" }} onChange={async e => {
                      const files = Array.from(e.target.files).slice(0, 3);
                      const existing = JSON.parse(user.work_photos || "[]");
                      const newUrls = [];
                      for (const f of files) { const url = await uploadPhoto(f, "work"); if (url) newUrls.push(url); }
                      const merged = [...existing, ...newUrls].slice(0, 12);
                      await sb.update("users", { work_photos: JSON.stringify(merged) }, `?id=eq.${user.id}`);
                      session.set({ ...user, work_photos: JSON.stringify(merged) }); window.location.reload();
                    }} />
                  </div>
                  {(() => {
                    const photos = JSON.parse(user.work_photos || "[]");
                    return photos.length === 0
                      ? <div style={{ textAlign: "center", padding: "24px 0", color: T.muted }}>
                          <p style={{ fontSize: 13, fontWeight: 600 }}>Add photos of your past work</p>
                          <p style={{ fontSize: 11, fontWeight: 500, marginTop: 4 }}>Homeowners hire contractors they can see</p>
                        </div>
                      : <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                          {photos.map((url, i) => (
                            <div key={i} style={{ aspectRatio: "1", borderRadius: 10, overflow: "hidden", border: `1px solid ${T.border}` }}>
                              <img src={url} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" />
                            </div>
                          ))}
                        </div>;
                  })()}
                </Card>

                {/* Google Reviews */}
                <Card T={T}>
                  <p style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 4 }}>Google Reviews</p>
                  <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginBottom: 14 }}>Paste your Google Business URL so homeowners can see your reviews.</p>
                  <Field label="Google Business URL" icon="" T={T}>
                    <input style={iS(T)} placeholder="maps.google.com/your-business" value={user.google_url || ""} onChange={async e => {
                      const val = e.target.value;
                      await sb.update("users", { google_url: val }, `?id=eq.${user.id}`);
                      session.set({ ...user, google_url: val });
                    }} />
                  </Field>
                  {user.google_url
                    ? <a href={user.google_url.startsWith("http") ? user.google_url : `https://${user.google_url}`} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 10, background: T.surface2, borderRadius: 10, padding: "12px 14px", textDecoration: "none", border: `1px solid ${T.border}` }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>View My Google Reviews</p>
                          <p style={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>Tap to open in Google Maps</p>
                        </div>
                        <span style={{ marginLeft: "auto", color: T.muted }}>›</span>
                      </a>
                    : <div style={{ background: T.goldGlow, border: `1px solid ${T.gold}40`, borderRadius: 10, padding: "10px 14px" }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: T.gold }}>Adding your Google reviews builds trust and gets you more jobs.</p>
                      </div>
                  }
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      {/* QUESTION MODAL */}
      {askingJob && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={e => e.target === e.currentTarget && setAskingJob(null)}>
          <div className="fu" style={{ background: T.card, borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxHeight: "88vh", overflowY: "auto", border: `1px solid ${T.border}` }}>
            <div style={{ width: 36, height: 4, background: T.border, borderRadius: 2, margin: "0 auto 20px" }} />
            <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 4 }}>Ask a Question</h3>
            <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 16 }}>{askingJob.title} · {askingJob.city}</p>
            <div style={{ marginBottom: 14 }}>
              <textarea
                placeholder="Ask the homeowner anything about the job before you bid..."
                value={questionText}
                onChange={e => setQuestionText(e.target.value)}
                style={{ width: "100%", minHeight: 100, padding: "12px 14px", background: T.surface2, border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: 13, color: T.text, outline: "none", lineHeight: 1.6 }}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="secondary" onClick={() => { setAskingJob(null); setQuestionText(""); }} T={T} style={{ width: 48, padding: "14px 0", flexShrink: 0 }}>←</Btn>
              <Btn onClick={async () => {
                if (!questionText.trim()) return;
                await sb.insert("messages", {
                  job_id: askingJob.id,
                  sender_id: user.id,
                  sender_name: user.business_name || `${user.first_name} ${user.last_name}`,
                  sender_role: "contractor",
                  message: questionText.trim(),
                  read: false,
                });
                await sb.insert("notifications", {
                  user_id: askingJob.homeowner_id,
                  message: `${user.business_name || user.first_name} has a question about your job "${askingJob.title}"`,
                  type: "question",
                  read: false,
                });
                setQuestionText("");
                setAskingJob(null);
                alert("Question sent! The homeowner will be notified.");
              }} T={T}>Send Question</Btn>
            </div>
          </div>
        </div>
      )}

      {/* BID MODAL */}
      {selectedJob && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={e => e.target === e.currentTarget && setSelectedJob(null)}>
          <div className="fu" style={{ background: T.card, borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxHeight: "88vh", overflowY: "auto", border: `1px solid ${T.border}` }}>
            <div style={{ width: 36, height: 4, background: T.border, borderRadius: 2, margin: "0 auto 20px" }} />
            <h3 style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 }}>Place Your Bid</h3>
            <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 16 }}>{selectedJob.title} · {selectedJob.city}</p>
            
            <div style={{ background: T.accentGlow, borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
            </div>
            <Field label="Your Bid Amount ($)" icon="" T={T}><input style={iS(T)} type="number" placeholder="e.g. 150" value={bidAmount} onChange={e => setBidAmount(e.target.value)} /></Field>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>Message to Homeowner</label>
              <textarea placeholder="Introduce yourself, describe your approach…" value={bidNote} onChange={e => setBidNote(e.target.value)} style={{ width: "100%", minHeight: 80, padding: "12px 14px", background: T.surface2, border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: 13, fontWeight: 500, color: T.text, outline: "none", lineHeight: 1.6 }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="secondary" onClick={() => setSelectedJob(null)} T={T} style={{ flex: 1 }}>Cancel</Btn>
              <Btn onClick={submitBid} disabled={!bidAmount || submitting || alreadyBid} T={T} style={{ flex: 2 }}>
                {submitting ? <div className="spin" style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", margin: "0 auto" }} /> : alreadyBid ? "Already Bid" : `Submit — $${bidAmount || "?"}`}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   HOMEOWNER DASHBOARD
───────────────────────────────────────────── */
function HomeownerDashboard({ T, dark, onToggleTheme, user, onLogout, defaultTab = "jobs", onHome, onSwitch }) {
  const [tab, setTab] = useState(defaultTab);
  const [hEditingProfile, setHEditingProfile] = useState(false);
  const [hEData, setHEData] = useState({});
  const [hSaving, setHSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const heu = (k, v) => setHEData(p => ({ ...p, [k]: v }));

  const hStartEdit = () => {
    setHEData({ firstName: user.first_name, lastName: user.last_name, city: user.city, phone: user.phone, address: user.address, zip: user.zip });
    setHEditingProfile(true);
  };

  const hSaveProfile = async () => {
    setHSaving(true);
    try {
      await sb.update("users", {
        first_name: hEData.firstName, last_name: hEData.lastName,
        city: hEData.city, phone: hEData.phone,
        address: hEData.address, zip: hEData.zip,
      }, `?id=eq.${user.id}`);
      session.set({ ...user, first_name: hEData.firstName, last_name: hEData.lastName, city: hEData.city, phone: hEData.phone, address: hEData.address, zip: hEData.zip });
      setHEditingProfile(false);
      window.location.reload();
    } catch {}
    setHSaving(false);
  };
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobBids, setJobBids] = useState([]);
  const [pStep, setPStep] = useState(0);
  const [pData, setPData] = useState({});
  const [posting, setPosting] = useState(false);
  const up = (k, v) => setPData(p => ({ ...p, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const [j, n] = await Promise.all([
        sb.select("jobs", `?homeowner_id=eq.${user.id}&order=created_at.desc`),
        sb.select("notifications", `?user_id=eq.${user.id}&order=created_at.desc`),
      ]);
      setMyJobs(j || []); setNotifications(n || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [tab]);

  const viewBids = async (job) => {
    setSelectedJob(job);
    const bids = await sb.select("bids", `?job_id=eq.${job.id}&order=amount.asc`);
    setJobBids(bids || []);
  };

  const acceptBid = async (bid) => {
    // Get contractor's phone number
    const contractorRows = await sb.select("users", `?id=eq.${bid.contractor_id}`);
    const contractor = contractorRows?.[0];
    await sb.update("jobs", { 
      status: "in_progress", 
      accepted_bid_id: bid.id, 
      accepted_contractor_id: bid.contractor_id,
      contractor_name: bid.contractor_name,
      contractor_phone: contractor?.phone || null,
    }, `?id=eq.${selectedJob.id}`);
    await sb.update("bids", { status: "accepted", homeowner_name: `${user.first_name} ${user.last_name}`, homeowner_phone: user.phone || null, homeowner_id: user.id, job_title: selectedJob.title }, `?id=eq.${bid.id}`);
    for (const b of jobBids.filter(b => b.id !== bid.id)) await sb.update("bids", { status: "declined" }, `?id=eq.${b.id}`);
    await sb.insert("notifications", {
      user_id: bid.contractor_id,
      message: `Your bid of $${bid.amount} was accepted for "${selectedJob.title}". Contact the homeowner: ${user.first_name} ${user.last_name} - ${user.phone || "check your app for details"}`,
      type: "accepted", read: false,
    });
    setSelectedJob(j => ({ ...j, status: "in_progress", accepted_contractor_id: bid.contractor_id, contractor_name: bid.contractor_name, contractor_phone: contractor?.phone }));
    load();
  };

  const completeJob = async (job) => {
    await sb.update("jobs", { status: "complete" }, `?id=eq.${job.id}`);
    setSelectedJob(null); load();
  };

  const deleteJob = async (job) => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    await sb.query("jobs", "DELETE", null, `?id=eq.${job.id}`);
    load();
  };

  const postJob = async () => {
    if (!pData.title) return;
    setPosting(true);
    try {
      await sb.insert("jobs", {
        homeowner_id: user.id,
        homeowner_name: `${user.first_name} ${user.last_name}`,
        homeowner_city: user.city,
        title: pData.title, trade: pData.category || "handyman",
        description: pData.description || "",
        city: user.city || "Marysville, OH",
        photo_url: (pData.photoUrls || [])[0] || null,
        video_url: pData.videoUrl || null,
        timeline: pData.timeline, status: "open", bid_count: 0,
      });
      setPData({}); setPStep(0); setTab("jobs"); load();
    } catch (e) { alert("Error posting job: " + e.message); }
    setPosting(false);
  };

  const statusColor = { open: T.accent, in_progress: T.gold, pending_approval: T.gold, complete: T.green, disputed: T.red };
  const statusLabel = { open: "Open", in_progress: "In Progress", pending_approval: "Pending Approval", complete: "Complete", disputed: "Disputed" };
  const mockJobImages = [UNSPLASH.job1, UNSPLASH.job2, UNSPLASH.job3];

  return (
    <div style={{ background: T.bg, minHeight: "100vh", paddingBottom: 80 }}>
      <Topbar T={T} user={user} onLogout={onLogout} dark={dark} onToggleTheme={onToggleTheme} onHome={onHome} onSwitch={onSwitch} />

      {/* Hero header */}
      <div style={{ position: "relative", margin: "16px 16px 0", borderRadius: 18, overflow: "hidden" }}>
        
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,rgba(5,150,105,0.8),rgba(6,95,70,0.8))" }} />
        <div style={{ position: "absolute", inset: 0, padding: "20px 20px" }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.65)", marginBottom: 4 }}>Welcome back </p>
          <p style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 14 }}>{user.first_name} {user.last_name}</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[["", myJobs.length, "My Jobs"], ["", myJobs.reduce((a, j) => a + (j.bid_count || 0), 0), "Bids Received"], ["", myJobs.filter(j => j.status === "complete").length, "Completed"]].map(([ic, v, l]) => (
              <div key={l} style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", borderRadius: 10, padding: "10px 8px", textAlign: "center", border: "1px solid rgba(255,255,255,0.15)" }}>
                <div style={{ fontSize: 16 }}>{ic}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#fff" }}>{v}</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 0.5, textTransform: "uppercase" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", margin: "14px 16px 0", background: T.surface, borderRadius: 12, padding: 4, border: `1px solid ${T.border}` }}>
        {[["jobs", "My Jobs"], ["post", "Post Job"], ["profile", "Profile"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "10px 4px", borderRadius: 10, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: tab === t ? T.green : "transparent", color: tab === t ? "#fff" : T.muted, transition: "all 0.2s" }}>{l}</button>
        ))}
      </div>

      <div style={{ padding: "14px 16px 0" }}>
        {loading && tab !== "post" ? <Spinner T={T} /> : (
          <>
            {tab === "jobs" && (
              <div className="fu">
                {myJobs.length === 0
                  ? <EmptyState T={T} icon="" title="No jobs posted yet" sub="Post your first job and get bids in minutes." action={<Btn onClick={() => setTab("post")} T={T} style={{ maxWidth: 220, margin: "0 auto" }}> Post a Job</Btn>} />
                  : myJobs.map((job, idx) => (
                    <div key={job.id} style={{ background: T.card, borderRadius: 16, marginBottom: 14, border: `1px solid ${T.cardBorder}`, boxShadow: T.shadow, overflow: "hidden" }}>
                      {job.photo_url && <img src={job.photo_url} alt="" style={{ width: "100%", height: 180, objectFit: "cover" }} />}
                      <div style={{ padding: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                          <p style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{job.title}</p>
                          <span style={{ background: statusColor[job.status] + "20", color: statusColor[job.status], border: `1px solid ${statusColor[job.status]}40`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>{statusLabel[job.status]}</span>
                        </div>
                        <p style={{ fontSize: 12, color: T.muted, fontWeight: 600, marginBottom: 10 }}> {job.city} · {timeAgo(job.created_at)}</p>
                        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                          {(() => { const cat = CATEGORIES.find(c => c.id === job.trade) || CATEGORIES[0]; return <span style={{ background: cat.color + "20", color: cat.color, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{cat.label}</span>; })()}
                          <span style={{ background: T.accentGlow, color: T.accent, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{job.bid_count || 0} bid{job.bid_count !== 1 ? "s" : ""}</span>
                        </div>
                        {job.status === "open" && <Btn onClick={() => viewBids(job)} variant={(job.bid_count || 0) > 0 ? "primary" : "secondary"} T={T}>{(job.bid_count || 0) > 0 ? `View ${job.bid_count} Bid${job.bid_count > 1 ? "s" : ""} →` : "Waiting for bids…"}</Btn>}
                        {job.status === "in_progress" && (() => {
                          return (
                            <div style={{ background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 12, padding: "14px 16px" }}>
                              <p style={{ fontSize: 13, fontWeight: 800, color: T.green, marginBottom: 10 }}>Bid Accepted! Here is your contractor's contact info:</p>
                              <a href={`tel:${job.contractor_phone}`} style={{ display: "flex", alignItems: "center", gap: 10, background: T.surface2, borderRadius: 10, padding: "12px 14px", textDecoration: "none", border: `1px solid ${T.border}` }}>
                                <div>
                                  <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{job.contractor_name}</p>
                                  <p style={{ fontSize: 12, color: T.accent, fontWeight: 600 }}>{job.contractor_phone || "Phone not available"}</p>
                                </div>
                                <span style={{ marginLeft: "auto", fontSize: 20 }}>📞</span>
                              </a>
                            </div>
                          );
                        })()}
                        {job.status === "pending_approval" && (
                          <div style={{ background: T.surface2, border: `1px solid ${T.gold}40`, borderRadius: 12, padding: "14px 16px" }}>
                            <p style={{ fontSize: 14, fontWeight: 800, color: T.gold, marginBottom: 6 }}>Job Marked Complete</p>
                            <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginBottom: 14, lineHeight: 1.6 }}>Your contractor has marked this job as complete. Please approve if you are satisfied or open a dispute if there is an issue. Payment releases automatically in 72 hours.</p>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={async () => {
                                await sb.update("jobs", { status: "complete" }, `?id=eq.${job.id}`);
                                setMyJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: "complete" } : j));
                              }} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: T.green, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Approve</button>
                              <button onClick={async () => {
                                await sb.update("jobs", { status: "disputed" }, `?id=eq.${job.id}`);
                                setMyJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: "disputed" } : j));
                                alert("Dispute opened. Kyle from HireHero will review and contact both parties within 24 hours.");
                              }} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1px solid ${T.red}`, background: T.redBg, color: T.red, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Dispute</button>
                            </div>
                          </div>
                        )}
                        {job.status === "disputed" && (
                          <div style={{ background: T.redBg, border: `1px solid ${T.red}40`, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                            <p style={{ fontSize: 13, fontWeight: 700, color: T.red }}>Dispute opened. HireHero will review within 24 hours.</p>
                          </div>
                        )}
                        {job.status === "complete" && <div style={{ background: T.greenBg, borderRadius: 10, padding: 10, textAlign: "center", fontSize: 13, fontWeight: 700, color: T.green }}> Job complete — payment released</div>}
                        {job.status === "open" && <button onClick={async () => { if (window.confirm("Delete this job?")) { await sb.delete("bids", `?job_id=eq.${job.id}`); await sb.delete("jobs", `?id=eq.${job.id}`); setMyJobs(prev => prev.filter(j => j.id !== job.id)); } }} style={{ width: "100%", marginTop: 8, padding: "10px", borderRadius: 10, border: `1px solid ${T.red}40`, background: T.redBg, color: T.red, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Delete Job</button>}
                      </div>
                    </div>
                  ))
                }
              </div>
            )}

            {tab === "post" && (
              <div className="fu">
                <Card T={T}>
                  {pStep === 0 && (
                    <div>
                      <h3 style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 }}>Post a Job</h3>
                      <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 18 }}>Add photos and describe what needs to be done.</p>

                      {/* Multiple photo upload */}
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, letterSpacing: 0.4, textTransform: "uppercase" }}>Job Photos (minimum 3, up to 5)</label>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                          {(pData.photoPreviews || []).map((src, i) => (
                            <div key={i} style={{ position: "relative", width: 80, height: 80, borderRadius: 10, overflow: "hidden", border: `2px solid ${T.border}` }}>
                              <img src={src} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" />
                              <button onClick={() => {
                                const newPreviews = (pData.photoPreviews || []).filter((_, j) => j !== i);
                                const newUrls = (pData.photoUrls || []).filter((_, j) => j !== i);
                                up("photoPreviews", newPreviews); up("photoUrls", newUrls);
                              }} style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,0.7)", border: "none", color: "#fff", fontSize: 10, cursor: "pointer" }}>x</button>
                            </div>
                          ))}
                          {(pData.photoPreviews || []).length < 5 && (
                            <div onClick={() => document.getElementById("jp").click()} style={{ width: 80, height: 80, borderRadius: 10, background: T.surface2, border: `2px dashed ${T.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                              <div style={{ fontSize: 24, color: T.muted }}>+</div>
                              <p style={{ fontSize: 9, fontWeight: 700, color: T.muted }}>Add Photo</p>
                            </div>
                          )}
                        </div>
                        <input id="jp" type="file" accept="image/*" multiple style={{ display: "none" }} onChange={async e => {
                          const files = Array.from(e.target.files).slice(0, 5 - (pData.photoPreviews || []).length);
                          for (const f of files) {
                            const r = new FileReader();
                            r.onload = ev => up("photoPreviews", [...(pData.photoPreviews || []), ev.target.result]);
                            r.readAsDataURL(f);
                            const url = await uploadPhoto(f, "jobs");
                            if (url) up("photoUrls", [...(pData.photoUrls || []), url]);
                          }
                          e.target.value = "";
                        }} />
                        {(pData.photoPreviews || []).length < 3 && (
                          <p style={{ fontSize: 11, color: T.red, fontWeight: 600, marginTop: 4 }}>At least 3 photos required so pros can give accurate bids</p>
                        )}
                        {(pData.photoPreviews || []).length >= 3 && (
                          <p style={{ fontSize: 11, color: T.green, fontWeight: 600, marginTop: 4 }}>Good — pros can now see your job clearly</p>
                        )}
                      </div>

                      {/* Video upload */}
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, letterSpacing: 0.4, textTransform: "uppercase" }}>Job Video (optional)</label>
                        <div onClick={() => document.getElementById("jv").click()} style={{ width: "100%", padding: "14px", borderRadius: 12, background: T.surface2, border: `2px dashed ${T.border}`, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                          {pData.videoUrl
                            ? <p style={{ fontSize: 13, fontWeight: 700, color: T.green }}>Video added</p>
                            : <>
                                <div style={{ fontSize: 24, color: T.muted }}>+</div>
                                <div>
                                  <p style={{ fontSize: 13, fontWeight: 700, color: T.muted }}>Add a walkthrough video</p>
                                  <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>30-60 seconds showing the full job</p>
                                </div>
                              </>
                          }
                        </div>
                        <input id="jv" type="file" accept="video/*" style={{ display: "none" }} onChange={async e => {
                          const f = e.target.files[0];
                          if (f) {
                            const url = await uploadPhoto(f, "videos");
                            if (url) up("videoUrl", url);
                          }
                        }} />
                      </div>

                      <Field label="What do you need done?" icon="" T={T}>
                        <select style={{ ...iS(T), cursor: "pointer", colorScheme: "dark" }} value={pData.category || ""} onChange={e => up("category", e.target.value)}>
                          <option value="" style={{ background: "#1e293b", color: "#fff" }}>Select a category...</option>
                          {CATEGORIES.map(c => <option key={c.id} value={c.id} style={{ background: "#1e293b", color: "#fff" }}>{c.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Job Title" icon="" T={T}><input style={iS(T)} placeholder="" value={pData.title || ""} onChange={e => up("title", e.target.value)} /></Field>

                      <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>Description</label>
                        <textarea placeholder="Additional details for your service pro." value={pData.description || ""} onChange={e => up("description", e.target.value)} style={{ width: "100%", minHeight: 90, padding: "12px 14px", background: T.surface2, border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: 13, fontWeight: 500, color: T.text, outline: "none", lineHeight: 1.6 }} />
                      </div>

                      <Btn onClick={() => pData.title && pData.category && (pData.photoPreviews || []).length >= 3 && setPStep(1)} disabled={!pData.title || !pData.category || (pData.photoPreviews || []).length < 3} T={T}>Continue</Btn>
                    </div>
                  )}
                  {pStep === 1 && (
                    <div>
                      <h3 style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 }}>Job Details</h3>
                      <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 18 }}>More detail = better, more accurate bids.</p>
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>Describe the Job</label>
                        <textarea placeholder="What needs to be done? Any details the handyman should know? Access instructions?" value={pData.description || ""} onChange={e => up("description", e.target.value)} style={{ width: "100%", minHeight: 100, padding: "12px 14px", background: T.surface2, border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: 13, fontWeight: 500, color: T.text, outline: "none", lineHeight: 1.6 }} />
                      </div>
                      <Field label="When do you need it?" icon="" T={T}>
                        <select style={{ ...iS(T), cursor: "pointer", colorScheme: "dark" }} value={pData.timeline || ""} onChange={e => up("timeline", e.target.value)}>
                          <option value="" style={{ background: "#1e293b", color: "#fff" }}>Select timeline…</option>
                          {["ASAP", "Within a week", "Within a month", "Flexible"].map(t => <option key={t} style={{ background: "#1e293b", color: "#fff" }}>{t}</option>)}
                        </select>
                      </Field>
                      <div style={{ display: "flex", gap: 10 }}>
                        <Btn variant="secondary" onClick={() => setPStep(0)} T={T} style={{ width: 48, padding: "14px 0", flexShrink: 0 }}>←</Btn>
                        <Btn onClick={() => postJob()} disabled={posting} T={T}>
                          {posting ? <div className="spin" style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", margin: "0 auto" }} /> : "Post & Get Bids"}
                        </Btn>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            )}

            {tab === "profile" && (
              <div className="fu">
                <Card T={T}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                    <div style={{ width: 60, height: 60, borderRadius: "50%", background: T.green + "30", border: `3px solid ${T.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
                      {user.first_name?.[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{user.first_name} {user.last_name}</p>
                      <p style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{user.city}</p>
                    </div>
                    <button onClick={() => hEditingProfile ? setHEditingProfile(false) : hStartEdit()} style={{ background: T.green + "20", border: `1.5px solid ${T.green}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: T.green, cursor: "pointer" }}>
                      {hEditingProfile ? "Cancel" : "Edit"}
                    </button>
                  </div>

                  {hEditingProfile && (
                    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", gap: 10 }}>
                        <Field label="First Name" icon="" T={T} style={{ flex: 1 }}><input style={iS(T)} value={hEData.firstName || ""} onChange={e => heu("firstName", e.target.value)} /></Field>
                        <Field label="Last Name" icon="" T={T} style={{ flex: 1 }}><input style={iS(T)} value={hEData.lastName || ""} onChange={e => heu("lastName", e.target.value)} /></Field>
                      </div>
                      <Field label="City" icon="" T={T}><input style={iS(T)} value={hEData.city || ""} onChange={e => heu("city", e.target.value)} /></Field>
                      <Field label="Phone" icon="" T={T}><input style={iS(T)} value={hEData.phone || ""} onChange={e => heu("phone", e.target.value)} /></Field>
                      <Field label="Address" icon="" T={T}><input style={iS(T)} value={hEData.address || ""} onChange={e => heu("address", e.target.value)} /></Field>
                      <Field label="Zip Code" icon="" T={T}><input style={iS(T)} value={hEData.zip || ""} onChange={e => heu("zip", e.target.value)} /></Field>
                      <Btn onClick={hSaveProfile} disabled={hSaving} T={T} variant="green">{hSaving ? "Saving..." : "Save Changes"}</Btn>
                    </div>
                  )}

                  {!hEditingProfile && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[["Phone", user.phone], ["Address", user.address], ["City", user.city], ["Zip", user.zip]].map(([l, v]) => v ? (
                        <div key={l} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: T.surface2, borderRadius: 10 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.text, flex: 1 }}>{l}</span>
                          <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>{v}</span>
                        </div>
                      ) : null)}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      {/* VIEW BIDS MODAL */}
      {selectedJob && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={e => e.target === e.currentTarget && setSelectedJob(null)}>
          <div className="fu" style={{ background: T.card, borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxHeight: "88vh", overflowY: "auto", border: `1px solid ${T.border}` }}>
            <div style={{ width: 36, height: 4, background: T.border, borderRadius: 2, margin: "0 auto 18px" }} />
            <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 4 }}>Bids for "{selectedJob.title}"</h3>
            <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 18 }}>{jobBids.length} contractor{jobBids.length !== 1 ? "s" : ""} responded · sorted by price</p>
            {jobBids.length === 0
              ? <EmptyState T={T} icon="⏳" title="Waiting for bids" sub="Handymen in your area will see your job and respond soon." />
              : jobBids.map(bid => {
                const accepted = selectedJob.accepted_contractor_id === bid.contractor_id;
                return (
                  <div key={bid.id} style={{ border: `1.5px solid ${accepted ? T.green : T.cardBorder}`, borderRadius: 14, padding: 16, marginBottom: 12, background: accepted ? T.greenBg : T.surface2 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
                      <div style={{ width: 46, height: 46, borderRadius: "50%", background: T.accentGlow, border: `2px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
                          <p style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{bid.contractor_name}</p>
                          {bid.contractor_verified && <span style={{ background: T.green + "20", color: T.green, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4 }}> VERIFIED</span>}
                        </div>
                        <p style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>{timeAgo(bid.created_at)}</p>
                      </div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: T.accent }}>${bid.amount}</div>
                    </div>
                    {bid.note && <div style={{ background: T.surface, borderRadius: 8, padding: "10px 12px", marginBottom: 12, border: `1px solid ${T.border}` }}><p style={{ fontSize: 13, color: T.muted, fontWeight: 500, lineHeight: 1.55, fontStyle: "italic" }}>"{bid.note}"</p></div>}
                    {!selectedJob.accepted_contractor_id && <Btn variant="green" onClick={() => acceptBid(bid)} T={T}> Accept This Bid — ${bid.amount}</Btn>}
                    {accepted && <div style={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: T.green }}> You hired this contractor</div>}
                  </div>
                );
              })
            }
            <Btn variant="secondary" onClick={() => setSelectedJob(null)} T={T} style={{ marginTop: 8 }}>Close</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRIVACY POLICY
───────────────────────────────────────────── */
function PrivacyScreen({ T, dark, onToggleTheme, onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: T.bg, paddingBottom: 40 }}>
      <Topbar T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={onBack} />
      <div style={{ padding: "24px 20px", maxWidth: 600, margin: "0 auto" }}>
        <p style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 }}>Privacy Policy</p>
        <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginBottom: 24 }}>Last updated May 2026</p>
        {[
          ["1. Information We Collect", "We collect information you provide when creating an account including your name, email address, phone number, city, and photos you upload. We also collect information about how you use the app such as jobs posted and bids submitted."],
          ["2. How We Use Your Information", "We use your information to operate HireHero, connect homeowners with service professionals, send notifications, and improve our services. We do not sell your personal information to third parties."],
          ["3. Information Shared Between Users", "When a bid is accepted, the homeowner and contractor will see each other name and phone number to coordinate the job. Your email is never shared with other users."],
          ["4. Photos", "Photos you upload are stored securely and visible to other users on the platform. Do not upload photos containing sensitive personal information."],
          ["5. Payment Information", "Payments are handled by Stripe. HireHero does not store your credit card or banking information. Please review Stripe privacy policy for how they handle payment data."],
          ["6. Data Security", "We use industry standard security measures to protect your information. Your password is encrypted and never stored in plain text."],
          ["7. Data Retention", "We retain your account information for as long as your account is active. You may request deletion by contacting kyle@usehirehero.com."],
          ["8. Children Privacy", "HireHero is not intended for users under 18 years of age. We do not knowingly collect information from children under 18."],
          ["9. Changes to This Policy", "We may update this privacy policy from time to time. Continued use of the platform after changes constitutes acceptance of the new policy."],
          ["10. Contact Us", "If you have questions about this privacy policy contact us at kyle@usehirehero.com"],
        ].map(([title, body]) => (
          <div key={title} style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 6 }}>{title}</p>
            <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, lineHeight: 1.7 }}>{body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TERMS OF SERVICE
───────────────────────────────────────────── */
function TermsScreen({ T, dark, onToggleTheme, onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: T.bg, paddingBottom: 40 }}>
      <Topbar T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={onBack} />
      <div style={{ padding: "24px 20px", maxWidth: 600, margin: "0 auto" }}>
        <p style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 }}>Terms of Service</p>
        <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginBottom: 24 }}>Last updated May 2026</p>

        {[
          ["1. About HireHero", "HireHero is an online marketplace platform that connects homeowners with independent contractors. HireHero is not a contractor, does not perform any services, and is not a party to any agreement between homeowners and contractors. HireHero is currently available in Ohio only."],
          ["2. Platform Only", "HireHero provides the technology to connect two parties. We do not control, supervise, or direct the work performed by contractors. All work is performed by independent contractors who are solely responsible for the quality, safety, and completion of their services."],
          ["3. No Liability for Work Performed", "HireHero is not liable for any damages, injuries, losses, or disputes arising from work performed by contractors found through our platform. By using HireHero, you agree that any claims related to work quality or disputes are between the homeowner and contractor only. HireHero's total liability to any user for any claim shall not exceed the total fees paid by that user to HireHero in the three months preceding the claim."],
          ["4. Background Checks", "HireHero does not perform background checks, criminal history searches, or credential verification on contractors beyond what is voluntarily submitted. HireHero does not guarantee that any contractor is licensed, insured, bonded, or qualified. Users are strongly encouraged to verify contractor credentials independently before hiring."],
          ["5. Contractor Responsibility", "Contractors are responsible for maintaining their own licenses, insurance, and compliance with local laws. Contractors agree to provide services in a professional and workmanlike manner."],
          ["6. Payment Cap", "The contractor's final charge cannot exceed the original accepted bid amount without explicit approval from the homeowner through the app. Any request for additional payment beyond the original bid must be submitted through HireHero and approved by the homeowner before additional work is performed or additional charges are collected. HireHero is not responsible for any unauthorized charges made outside the platform."],
          ["7. No-Show Policy", "If a contractor fails to show up for a scheduled job without prior notice the homeowner may report the no-show through the platform. First offense will result in a warning. A second no-show offense will result in permanent removal from the platform. Contractors who repeatedly cancel or fail to communicate with homeowners are subject to removal at HireHero's discretion."],
          ["8. Payments", "HireHero facilitates payments between parties through third party payment processors. We are not responsible for disputes related to payment amounts. All payment disputes must be resolved between the homeowner and contractor with HireHero acting only as a mediator at our discretion."],
          ["9. User Conduct", "Users agree not to use HireHero for any unlawful purpose, post false or misleading information, solicit payments outside the platform to avoid fees, or engage in any behavior that harms other users or the platform. HireHero reserves the right to remove any user at any time for any reason."],
          ["10. Dispute Resolution", "HireHero may at our discretion assist in mediating disputes between homeowners and contractors. Our decision in any dispute is final. HireHero is not obligated to mediate any dispute. Users agree to attempt to resolve disputes directly before involving HireHero."],
          ["11. Founding Member Pricing", "Founding member pricing is offered to early adopters at a reduced rate. This rate is locked in for as long as the account remains active and in good standing. If a founding member cancels their subscription the founding member rate is forfeited and cannot be reinstated. Future pricing for new members may differ."],
          ["12. Indemnification", "You agree to indemnify and hold harmless HireHero, its owners, employees, and agents from any claims, damages, losses, or expenses including legal fees arising from your use of the platform, your violation of these terms, or your interactions with other users."],
          ["13. Changes to Terms", "HireHero reserves the right to update these terms at any time. Continued use of the platform after changes constitutes acceptance of the new terms."],
          ["14. Contact", "For questions about these terms contact kyle@usehirehero.com"],
        ].map(([title, body]) => (
          <div key={title} style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 6 }}>{title}</p>
            <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, lineHeight: 1.7 }}>{body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
const ADMIN_PIN = "1234";

function AdminScreen({ T, dark, onToggleTheme, onBack }) {
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [adminTab, setAdminTab] = useState("contractors");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cRows, jRows] = await Promise.all([
        sb.select("users", "?role=eq.contractor&order=created_at.desc"),
        sb.select("jobs", "?order=created_at.desc"),
      ]);
      setContractors(cRows || []);
      setJobs(jRows || []);
    } catch {}
    setLoading(false);
  };

  const toggleVerify = async (contractor) => {
    const newVal = !contractor.verified;
    await sb.update("users", { verified: newVal }, `?id=eq.${contractor.id}`);
    setMsg(`${contractor.first_name} ${newVal ? "verified" : "unverified"}`);
    setTimeout(() => setMsg(""), 3000);
    load();
  };

  const deleteContractor = async (contractor) => {
    if (!window.confirm(`Delete ${contractor.first_name} ${contractor.last_name}?`)) return;
    await sb.delete("users", `?id=eq.${contractor.id}`);
    setContractors(prev => prev.filter(c => c.id !== contractor.id));
    setMsg(`${contractor.first_name} deleted`);
    setTimeout(() => setMsg(""), 3000);
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete job "${job.title}"? This cannot be undone.`)) return;
    // Delete bids first then the job
    await sb.delete("bids", `?job_id=eq.${job.id}`);
    await sb.delete("jobs", `?id=eq.${job.id}`);
    setJobs(prev => prev.filter(j => j.id !== job.id));
    setMsg(`Job "${job.title}" deleted`);
    setTimeout(() => setMsg(""), 3000);
  };

  if (!authed) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <p style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 24 }}>Admin Access</p>
      <div style={{ width: "100%", maxWidth: 320 }}>
        <Field label="PIN" icon="" T={T}>
          <input style={iS(T)} type="password" placeholder="" value={pin} onChange={e => setPin(e.target.value)} onKeyDown={e => e.key === "Enter" && (pin === ADMIN_PIN ? (setAuthed(true), load()) : setPin(""))} />
        </Field>
        <Btn onClick={() => pin === ADMIN_PIN ? (setAuthed(true), load()) : setPin("")} T={T}>Enter</Btn>
        <Btn variant="secondary" onClick={onBack} T={T} style={{ marginTop: 10 }}>Back</Btn>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bg, paddingBottom: 40 }}>
      <Topbar T={T} dark={dark} onToggleTheme={onToggleTheme} onBack={onBack} />
      <div style={{ padding: "20px 16px" }}>
        <p style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 }}>Admin Panel</p>
        <p style={{ fontSize: 13, color: T.muted, fontWeight: 500, marginBottom: 14 }}>Manage your platform</p>

        {/* Stats Dashboard */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ background: T.card, borderRadius: 12, padding: "14px 16px", border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 24, fontWeight: 800, color: T.accent }}>{contractors.length}</p>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Service Pros</p>
          </div>
          <div style={{ background: T.card, borderRadius: 12, padding: "14px 16px", border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 24, fontWeight: 800, color: T.green }}>{jobs.length}</p>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Jobs</p>
          </div>
        </div>

        {/* Pros by category */}
        <div style={{ background: T.card, borderRadius: 12, padding: "14px 16px", border: `1px solid ${T.border}`, marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 12 }}>Service Pros by Category</p>
          {CATEGORIES.map(cat => {
            const count = contractors.filter(c => {
              try { return (JSON.parse(c.trades || "[]")).includes(cat.id); } catch { return false; }
            }).length;
            return (
              <div key={cat.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{cat.label}</p>
                <span style={{ background: count > 0 ? cat.color + "20" : T.surface2, color: count > 0 ? cat.color : T.muted, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{count} pro{count !== 1 ? "s" : ""}</span>
              </div>
            );
          })}
        </div>

        {/* Admin Tabs */}
        <div style={{ display: "flex", background: T.surface, borderRadius: 12, padding: 4, border: `1px solid ${T.border}`, marginBottom: 16 }}>
          {[["contractors", "Contractors"], ["jobs", "Jobs"]].map(([t, l]) => (
            <button key={t} onClick={() => setAdminTab(t)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", background: adminTab === t ? T.accent : "transparent", color: adminTab === t ? "#fff" : T.muted }}>{l}</button>
          ))}
        </div>

        {msg && <div style={{ background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}><p style={{ fontSize: 13, fontWeight: 700, color: T.green }}>{msg}</p></div>}

        {loading ? <Spinner T={T} /> : (
          <>
            {adminTab === "contractors" && (
              contractors.length === 0
                ? <EmptyState T={T} icon="" title="No contractors yet" sub="Contractors will appear here once they sign up." />
                : contractors.map(c => (
                  <div key={c.id} style={{ background: T.card, borderRadius: 14, padding: 16, marginBottom: 12, border: `1.5px solid ${c.verified ? T.green : T.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: "50%", background: T.accentGlow, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{c.first_name?.[0]}</div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{c.first_name} {c.last_name}</p>
                        <p style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{c.email}</p>
                        <p style={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>{c.business_name} · {c.city}</p>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: c.verified ? T.green + "20" : T.gold + "20", color: c.verified ? T.green : T.gold }}>
                        {c.verified ? "Verified" : "Pending"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn variant={c.verified ? "secondary" : "primary"} onClick={() => toggleVerify(c)} T={T} style={{ flex: 2 }}>
                        {c.verified ? "Remove Verification" : "Verify Contractor"}
                      </Btn>
                      <button onClick={() => deleteContractor(c)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1.5px solid ${T.red}40`, background: T.redBg, color: T.red, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Delete</button>
                    </div>
                  </div>
                ))
            )}

            {adminTab === "jobs" && (
              jobs.length === 0
                ? <EmptyState T={T} icon="" title="No jobs yet" sub="Jobs will appear here once homeowners start posting." />
                : jobs.map(job => (
                  <div key={job.id} style={{ background: T.card, borderRadius: 14, padding: 16, marginBottom: 12, border: `1px solid ${T.border}` }}>
                    {job.photo_url && <img src={job.photo_url} alt="" style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 10, marginBottom: 12 }} />}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <p style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{job.title}</p>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: T.accentGlow, color: T.accent }}>{job.status}</span>
                    </div>
                    <p style={{ fontSize: 12, color: T.muted, fontWeight: 600, marginBottom: 4 }}>{job.homeowner_name} · {job.city}</p>
                    <p style={{ fontSize: 12, color: T.muted, fontWeight: 500, marginBottom: 12, lineHeight: 1.5 }}>{job.description}</p>
                    <button onClick={() => deleteJob(job)} style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1.5px solid ${T.red}40`, background: T.redBg, color: T.red, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Delete Job</button>
                  </div>
                ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ROOT APP
───────────────────────────────────────────── */
export default function App() {
  const [screen, setScreen] = useState("loading");
  const [role, setRole] = useState(null);
  const [user, setUser] = useState(null);
  const [defaultTab, setDefaultTab] = useState("jobs");
  const [dark, setDark] = useState(getTheme() === "dark");
  const [logoTaps, setLogoTaps] = useState(0);

  const T = dark ? DARK : LIGHT;

  useEffect(() => {
    const s = session.get();
    if (s) {
      sb.select("users", `?email=eq.${encodeURIComponent(s.email)}&select=*`).then(rows => {
        if (rows && rows.length > 1) {
          session.clear();
          setScreen("welcome");
        } else {
          setUser(s); setRole(s.role); setScreen("dashboard");
        }
      }).catch(() => {
        setUser(s); setRole(s.role); setScreen("dashboard");
      });
    } else setScreen("welcome");
  }, []);

  const handleLogoTap = () => {
    const next = logoTaps + 1;
    setLogoTaps(next);
    if (next >= 5) { setLogoTaps(0); setScreen("admin"); }
    setTimeout(() => setLogoTaps(0), 3000);
  };

  const goHome = () => setScreen("welcome");

  useEffect(() => {
    document.body.style.background = T.bg;
    document.body.style.color = T.text;
  }, [dark]);

  const toggleTheme = () => { const nd = !dark; setDark(nd); setTheme(nd ? "dark" : "light"); };
  const switchAccount = async () => {
    if (!user) return;
    const rows = await sb.select("users", `?email=eq.${encodeURIComponent(user.email)}&select=*`);
    if (rows && rows.length > 1) {
      const other = rows.find(r => r.id !== user.id);
      if (other) { session.set(other); setUser(other); setRole(other.role); setScreen("dashboard"); }
    }
  };

  const logout = () => { session.clear(); setUser(null); setRole(null); setScreen("welcome"); };
  const login = (u) => { session.set(u); setUser(u); setRole(u.role); setScreen("dashboard"); };
  const signup = (u) => { setUser(u); setRole(u.role); setDefaultTab(u.role === "homeowner" ? "post" : "jobs"); setScreen("dashboard"); };

  const shared = { T, dark, onToggleTheme: toggleTheme };

  if (screen === "loading") return <><style>{css}</style><div style={{ background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner T={T} text="Loading HireHero…" /></div></>;

  return (
    <>
      <style>{css}</style>
      <style>{`
        #hirehero-root {
          max-width: 480px;
          margin: 0 auto;
          min-height: 100vh;
          box-shadow: 0 0 60px rgba(0,0,0,0.15);
          position: relative;
        }
        body { background: #0a0a0a !important; }
      `}</style>
      <div id="hirehero-root">
      {screen === "welcome" && <WelcomeScreen {...shared} onSelect={(r, s) => { setRole(r); setScreen(s); }} onLogin={() => setScreen("login")} onDashboard={user ? () => setScreen("dashboard") : null} onLogoTap={handleLogoTap} onShowTerms={() => setScreen("terms")} onShowPrivacy={() => setScreen("privacy")} />}
      {screen === "login" && <LoginScreen {...shared} onBack={() => setScreen("welcome")} onLogin={login} />}
      {screen === "signup" && role === "contractor" && <ContractorSignup {...shared} onDone={signup} onLogin={() => setScreen("login")} onBack={() => setScreen("welcome")} />}
      {screen === "signup" && role === "homeowner" && <HomeownerSignup {...shared} onDone={signup} onLogin={() => setScreen("login")} onBack={() => setScreen("welcome")} />}
      {screen === "dashboard" && user?.role === "contractor" && <ContractorDashboard {...shared} user={user} onLogout={logout} onHome={goHome} onSwitch={switchAccount} />}
      {screen === "dashboard" && user?.role === "homeowner" && <HomeownerDashboard {...shared} user={user} onLogout={logout} defaultTab={defaultTab} onHome={goHome} onSwitch={switchAccount} />}
      {screen === "admin" && <AdminScreen {...shared} onBack={() => setScreen("welcome")} />}
      {screen === "terms" && <TermsScreen {...shared} onBack={() => setScreen("welcome")} />}
      {screen === "privacy" && <PrivacyScreen {...shared} onBack={() => setScreen("welcome")} />}
      </div>
    </>
  );
}
